/**
 * WeeklyPlanner.jsx
 * =================
 * 
 * Order Pipeline Dashboard for the Cashel Blue Operations Hub.
 * Manages the complete order lifecycle from creation to dispatch.
 * 
 * ## Core Features:
 * - **Calendar View**: Day, Week, or Month view of orders by dispatch date
 * - **Order Pipeline**: Visual Kanban-style columns (Pending → Confirmed → Ready → Dispatched)
 * - **Quick Actions**: Create orders, manage batches, dispatch notes
 * - **Status Workflow**: Smart status transitions with batch creation integration
 * 
 * ## Order Status Flow:
 * 1. **Pending** - Initial order entry
 * 2. **Confirmed** - Customer order confirmed
 * 3. **Ready** - Triggers batch creation dialog (BatchCreationDialog component)
 * 4. **Dispatched** - Final dispatch note generated
 * 
 * ## Extracted Components:
 * - CreateOrderDialog - New order creation
 * - ViewOrderDialog - View/edit existing orders
 * - QuickDispatchDialog - Quick dispatch for simple orders
 * - RevertStatusDialog - Revert order status with batch handling
 * - BatchCreationDialog - Multi-step batch creation (Ready status)
 * 
 * ## State Management:
 * - Local state for dialogs, filters, and UI
 * - API calls via ../lib/api.js
 * - Auth context for user permissions
 * 
 * @author Cashel Blue Operations Hub Team
 * @lastModified February 21, 2025
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  getDispatches, 
  getCustomers,
  getProducts,
  getBatches,
  getCoBrandings,
  createDispatch,
  updateDispatch,
  updateDispatchStatus,
  createCustomer,
  createProduct,
  createBatch,
  createCoBranding,
  checkOrderStatusChange,
  revertOrderStatus,
  getDispatchEmailTemplate,
  sendDispatchEmail,
  sendCOAEmail,
  sendPalletLabelEmail,
  getCustomerEmailRecipients,
  toggleInvoiced,
  bulkInvoiced,
  openDispatchPdf,
  openPalletLabels,
  openCoaPdf,
  getDispatch,
} from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../lib/utils';
import { openPrintWindow } from '../lib/printWindow';
import RecordReturnDialog from '../components/RecordReturnDialog';
import { SplitFulfilmentDialog } from '../components/phased/SplitFulfilmentDialog';
import { PoPartsTree, PoPartChip } from '../components/phased/PoPartsTree';
import { PoSummaryDialog } from '../components/phased/PoSummaryDialog';
import { SPLITTABLE_STATUSES, announceAdjustments } from '../components/phased/phasedUtils';
import AccountantViewDialog from '../components/AccountantViewDialog';
import { InvoicePreviewDialog } from '../components/accountsiq/InvoicePreviewDialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { DatePicker } from '../components/ui/date-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../components/ui/dropdown-menu';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Plus, 
  Loader2, 
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Truck,
  Clock,
  CheckCircle2,
  Circle,
  Check,
  Play,
  Printer,
  Calendar as CalendarIcon,
  LayoutGrid,
  CalendarRange,
  Package,
  User,
  Boxes,
  Tags,
  Eye,
  Edit,
  AlertTriangle,
  Lock,
  Trash2,
  Shield,
  Info,
  MoreVertical,
  FileText,
  FileSpreadsheet,
  ArrowLeft,
  ArrowRight,
  Mail,
  Plug,
  Send,
  Search,
  X as XIcon,
  ExternalLink,
  ScrollText,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Undo2,
  Split,
  GitBranch
} from 'lucide-react';

// Import extracted dialog components for refactored Weekly Planner
import { getStatusInfo } from './planner/constants';
import CreateOrderDialog from './planner/CreateOrderDialog';
import ViewOrderDialog from './planner/ViewOrderDialog';
import QuickDispatchDialog from './planner/QuickDispatchDialog';
import RevertStatusDialog from './planner/RevertStatusDialog';
import BatchCreationDialog from './planner/BatchCreationDialog';
import { EmailPromptDialog, EmailComposeDialog } from './planner/EmailDialogs';
import AldiBranchDialog from '../components/AldiBranchDialog';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  addWeeks, 
  subWeeks, 
  addMonths,
  subMonths,
  addDays,
  subDays,
  getISOWeek,
  getISOWeekYear,
  isWithinInterval, 
  parse,
  eachDayOfInterval,
  isSameDay
} from 'date-fns';

const ORDER_STATUSES = [
  { value: 'not_started', label: 'Not Started', color: 'bg-red-100 text-red-700', icon: Circle },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: Play },
  { value: 'locked', label: 'Ready', color: 'bg-blue-100 text-blue-700', icon: Clock },
  { value: 'dispatched', label: 'Dispatched', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
];

const VIEW_MODES = [
  { value: 'day', label: 'Day', icon: CalendarIcon },
  { value: 'week', label: 'Week', icon: CalendarRange },
  { value: 'month', label: 'Month', icon: LayoutGrid },
];

// Units as entered at Sales Order Entry (each unit is one physical handling unit).
const dispUnits = (d) => Number(d.total_ordered_qty) || 0;
const fmtUnits = (n) => (Number.isInteger(n) ? n : Number(n.toFixed(1))).toLocaleString('en-IE');
// Orders store dispatch_date as dd/MM/yyyy or yyyy-MM-dd; show and group by dd/MM/yyyy.
const showDate = (str) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (str || '');
};

export default function WeeklyPlanner() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dispatches, setDispatches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [cobrandings, setCobrandings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');

  // Honour ?date=YYYY-MM-DD or ?date=DD/MM/YYYY on first load (e.g. from Reports → OOS Orders).
  // Jumps the planner to that date in day view.
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (!dateParam) return;
    let parsed = parse(dateParam, 'yyyy-MM-dd', new Date());
    if (isNaN(parsed.getTime())) parsed = parse(dateParam, 'dd/MM/yyyy', new Date());
    if (isNaN(parsed.getTime())) parsed = new Date(dateParam);
    if (!isNaN(parsed.getTime())) {
      setCurrentDate(parsed);
      setViewMode('day');
      // Highlight scroll target if a dispatch id was also passed
      const targetId = searchParams.get('dispatch');
      if (targetId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-dispatch-row="${targetId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-amber-400');
            setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 3000);
          }
        }, 1200);
      }
      // Strip params so subsequent navigations don't keep jumping
      const next = new URLSearchParams(searchParams);
      next.delete('date');
      next.delete('dispatch');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = no search active
  
  // Create Order Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    customer_id: '',
    product_id: '',
    cobranding_id: '',
    quantity: '',
    estimated_weight: '',
    po_number: '',
    dispatch_date: '',
    transport_agent: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Pipeline table sorting (persisted per-browser)
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('planner.sortConfig');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.key === 'string' && (parsed.dir === 'asc' || parsed.dir === 'desc')) {
          return parsed;
        }
      }
    } catch { /* ignore */ }
    return { key: 'dispatch_date', dir: 'asc' };
  });

  useEffect(() => {
    try {
      localStorage.setItem('planner.sortConfig', JSON.stringify(sortConfig));
    } catch { /* quota / private mode — ignore */ }
  }, [sortConfig]);

  // Inline creation dialogs
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showCreateCoBranding, setShowCreateCoBranding] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newProductName, setNewProductName] = useState('');
  const [newProductWeight, setNewProductWeight] = useState('');
  const [newProductUnits, setNewProductUnits] = useState('1');
  const [newCoBrandingName, setNewCoBrandingName] = useState('');
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [creatingCoBranding, setCreatingCoBranding] = useState(false);

  // Ready Status Dialog - Multi-step batch creation
  const [readyDialogOpen, setReadyDialogOpen] = useState(false);
  const [pendingReadyDispatch, setPendingReadyDispatch] = useState(null);
  const [batchLines, setBatchLines] = useState([]);
  const [creatingBatch, setCreatingBatch] = useState(false);
  // Actual pallet count entered on the Ready dialog — overrides the SO estimate.
  // Pre-filled from the sales-order value; persists to dispatch.num_pallets so it
  // shows on the printed Dispatch Note and downstream Transport reports.
  const [palletCountReady, setPalletCountReady] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  // When TRUE the batch entry dialog is being re-opened to EDIT an already-Ready/dispatched
  // order's batch lines. On Confirm we update lines in-place WITHOUT changing dispatch status
  // and WITHOUT recreating batches that already exist with the same code.
  const [editingBatchData, setEditingBatchData] = useState(false);
  // New: 3-step batch creation flow
  // Step 1: Enter batch codes and quantities for each product
  // Step 2: Enter use-by dates (one per product)
  // Step 3: Review and save
  const [batchStep, setBatchStep] = useState(1);
  const [productBatches, setProductBatches] = useState({}); // { productIndex: [{ batch_code, quantity }] }
  const [productUseDates, setProductUseDates] = useState({}); // { productIndex: use_by_date }
  const [productNotes, setProductNotes] = useState({}); // { productIndex: notes_text }
  const [batchValidationWarnings, setBatchValidationWarnings] = useState([]); // Soft warnings for batch creation
  const [showBatchWarningConfirm, setShowBatchWarningConfirm] = useState(false); // Show warning confirmation dialog
  const [outOfStockProducts, setOutOfStockProducts] = useState({}); // { productIndex: boolean }

  // Quick Dispatch Dialog (when changing to dispatched status)
  const [quickDispatchOpen, setQuickDispatchOpen] = useState(false);
  const [pendingDispatchId, setPendingDispatchId] = useState(null);
  
  // Uplift Date dialog (shown when triggering dispatched)
  const [upliftDialogOpen, setUpliftDialogOpen] = useState(false);
  const [upliftDate, setUpliftDate] = useState(null);
  const [upliftDispatchRef, setUpliftDispatchRef] = useState(null); // dispatch being dispatched
  
  const [dispatchLines, setDispatchLines] = useState([{ 
    product_id: '', 
    batch_id: '', 
    batch_code: '',
    use_by_date: '',
    quantity: '', 
    weight_kg: '', 
    cases: '',
    format: '',
    cobranding_id: '',
    notes: ''
  }]);

  // View/Edit Order Dialog
  const [viewOrderDialogOpen, setViewOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(false);
  const [orderFormData, setOrderFormData] = useState({});
  const [savingOrder, setSavingOrder] = useState(false);

  // Accountant view (AccountsIQ invoice helper) — opened from the row 3-dot menu.
  // We stash the full dispatch object so the dialog can run its own
  // line aggregation without re-fetching.
  const [accountantDispatch, setAccountantDispatch] = useState(null);
  const [accountantOpen, setAccountantOpen] = useState(false);

  // Status Revert Dialog
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertCheckResult, setRevertCheckResult] = useState(null);
  const [pendingRevertDispatch, setPendingRevertDispatch] = useState(null);
  const [pendingRevertStatus, setPendingRevertStatus] = useState(null);
  const [batchAction, setBatchAction] = useState('delete');  // Default to DELETE - user requirement
  const [batchDeleteReason, setBatchDeleteReason] = useState('Order status reverted');
  const [overrideReason, setOverrideReason] = useState('');
  const [reverting, setReverting] = useState(false);

  // Email on Dispatch state
  const [emailPromptOpen, setEmailPromptOpen] = useState(false);
  const [emailPromptDispatch, setEmailPromptDispatch] = useState(null);
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [aldiBranchOpen, setAldiBranchOpen] = useState(false);
  const [aldiBranchDispatchId, setAldiBranchDispatchId] = useState(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDispatch, setReturnDispatch] = useState(null);
  const [emailData, setEmailData] = useState({ to_email: '', cc_emails: [], subject: '', body: '', attach_pdf: true });
  const [emailCcInput, setEmailCcInput] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  // Which document is being emailed from the unified compose dialog:
  // 'dispatch_note' (default), 'coa', or 'pallet_label'
  const [emailDocType, setEmailDocType] = useState('dispatch_note');
  // Suggested recipients fetched from the customer record — auto-learned by the
  // recipient-recording hook on every successful email send.
  const [emailRecipientSuggestions, setEmailRecipientSuggestions] = useState([]);
  // Dispatch whose AccountsIQ invoice preview is open.
  const [aiqPreviewDispatch, setAiqPreviewDispatch] = useState(null);
  // Per-dispatch in-flight flag for invoice toggle
  const [togglingInvoice, setTogglingInvoice] = useState(null);
  const [bulkInvoicingBusy, setBulkInvoicingBusy] = useState(false);

  // Phased dispatch: Split Fulfilment dialog, PO summary and expanded PO trees
  const [splitTarget, setSplitTarget] = useState(null); // { dispatch, prefill, fromReady }
  const [poSummaryGroupId, setPoSummaryGroupId] = useState(null);
  const [expandedPo, setExpandedPo] = useState(() => new Set());
  const toggleExpandedPo = (id) => setExpandedPo(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleToggleInvoiced = async (dispatch) => {
    if (togglingInvoice === dispatch.id) return;
    const willInvoice = !dispatch.invoiced;
    // Confirm if un-invoicing an AIQ-pushed order
    if (!willInvoice && dispatch.accountsiq_transaction_id) {
      const ok = window.confirm(
        `This order was pushed to AccountsIQ (tx: ${dispatch.accountsiq_transaction_id}).\n\n` +
        `Un-ticking only clears the local "invoiced" flag — the invoice in AccountsIQ is NOT cancelled.\n\nContinue?`,
      );
      if (!ok) return;
    }
    setTogglingInvoice(dispatch.id);
    try {
      await toggleInvoiced(dispatch.id, willInvoice);
      toast.success(willInvoice ? 'Marked as Invoiced' : 'Marked as Not Invoiced');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update invoice flag');
    } finally {
      setTogglingInvoice(null);
    }
  };

  const handleBulkInvoiced = async (target) => {
    // target: true to mark all visible as invoiced, false to un-mark
    const eligible = filteredDispatches.filter(
      (d) => ['ready', 'locked', 'dispatched'].includes(d.status) && (!!d.invoiced) !== target,
    );
    if (eligible.length === 0) {
      toast.info(target ? 'Nothing to mark — all visible eligible orders already invoiced.' : 'Nothing to un-mark — no invoiced orders visible.');
      return;
    }
    const aiqCount = target ? 0 : eligible.filter((d) => d.accountsiq_transaction_id).length;
    const msg = target
      ? `Mark ${eligible.length} visible order(s) as Invoiced?`
      : `Un-mark ${eligible.length} visible order(s) as Not Invoiced?` +
        (aiqCount ? `\n\n⚠ ${aiqCount} of those have AccountsIQ invoices already — un-ticking will NOT cancel them in AccountsIQ.` : '');
    if (!window.confirm(msg)) return;
    setBulkInvoicingBusy(true);
    try {
      const { data } = await bulkInvoiced(eligible.map((d) => d.id), target);
      toast.success(`${data.updated} order(s) updated${data.skipped_ineligible ? ` (${data.skipped_ineligible} skipped)` : ''}`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkInvoicingBusy(false);
    }
  };

  // Calculate date ranges based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'day') {
      return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
    } else if (viewMode === 'week') {
      return { start: startOfWeek(currentDate, { weekStartsOn: 1 }), end: endOfWeek(currentDate, { weekStartsOn: 1 }) };
    } else {
      return { start: startOfMonth(currentDate), end: endOfMonth(currentDate) };
    }
  }, [currentDate, viewMode]);

  const weekNumber = getISOWeek(currentDate);
  const year = getISOWeekYear(currentDate);
  const monthName = format(currentDate, 'MMMM yyyy');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load each data source independently to prevent one failure from blocking all
      // Note: Products need to include all (active + inactive) since orders may reference inactive products
      const [dispatchesRes, customersRes, productsRes, batchesRes, cobrandingsRes] = await Promise.allSettled([
        getDispatches(),
        getCustomers({ is_active: true }),
        getProducts(), // Include all products - orders may reference inactive ones
        getBatches({ available_only: true }),
        getCoBrandings({ is_active: true })
      ]);
      
      // Set data from successful responses
      if (dispatchesRes.status === 'fulfilled') setDispatches(dispatchesRes.value.data || []);
      if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data || []);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data || []);
      if (batchesRes.status === 'fulfilled') setBatches(batchesRes.value.data || []);
      if (cobrandingsRes.status === 'fulfilled') setCobrandings(cobrandingsRes.value.data || []);
      
      // Only show error if dispatches failed (critical)
      if (dispatchesRes.status === 'rejected') {
        toast.error('Failed to load orders. Please refresh the page.');
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter dispatches for current range
  // dispatch_date exists in BOTH dd/MM/yyyy (UI-entered) and yyyy-MM-dd (API/legacy) formats.
  const parseDispatchDate = (str) => {
    if (!str) return null;
    let d = parse(str, 'dd/MM/yyyy', new Date());
    if (isNaN(d.getTime())) d = parse(str.slice(0, 10), 'yyyy-MM-dd', new Date());
    return isNaN(d.getTime()) ? null : d;
  };
  const filteredDispatches = useMemo(() => {
    const inRange = dispatches.filter(d => {
      const dispatchDate = parseDispatchDate(d.dispatch_date);
      if (!dispatchDate) return false;
      try {
        return isWithinInterval(dispatchDate, { start: dateRange.start, end: dateRange.end });
      } catch {
        return false;
      }
    });

    // Map sort key to a comparable accessor
    const STATUS_ORDER = { draft: 0, in_progress: 1, locked: 2, dispatched: 3 };
    const accessor = (d) => {
      switch (sortConfig.key) {
        case 'po_number':
          return String(d.po_number || d.dispatch_number || '').toLowerCase();
        case 'customer':
          return String(d.customer_name || '').toLowerCase();
        case 'transport':
          return String(d.vehicle_info || '').toLowerCase();
        case 'status':
          return STATUS_ORDER[d.status] ?? 99;
        case 'units':
          return dispUnits(d);
        case 'dispatch_date':
        default:
          return parseDispatchDate(d.dispatch_date)?.getTime() || 0;
      }
    };

    const dir = sortConfig.dir === 'desc' ? -1 : 1;
    return [...inRange].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      // Stable secondary sort: dispatch date asc, then dispatch_number
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      // tie-breaker: dispatch_date asc
      const da = parseDispatchDate(a.dispatch_date)?.getTime() || 0;
      const db = parseDispatchDate(b.dispatch_date)?.getTime() || 0;
      if (da !== db) return da - db;
      return String(a.dispatch_number || '').localeCompare(String(b.dispatch_number || ''));
    });
  }, [dispatches, dateRange, sortConfig]);

  // Unscheduled dispatches (no date set)
  const unscheduledDispatches = useMemo(() => {
    return dispatches.filter(d => !d.dispatch_date && d.status !== 'dispatched');
  }, [dispatches]);

  // Group by day for month view
  const dispatchesByDay = useMemo(() => {
    const grouped = {};
    filteredDispatches.forEach(d => {
      const day = showDate(d.dispatch_date);
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(d);
    });
    return grouped;
  }, [filteredDispatches]);

  // Get days in current month for calendar grid
  const daysInRange = useMemo(() => {
    if (viewMode === 'month') {
      return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
    }
    return [];
  }, [viewMode, dateRange]);

  const goToPrevious = () => {
    if (viewMode === 'day') setCurrentDate(subDays(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subMonths(currentDate, 1));
  };

  const goToNext = () => {
    if (viewMode === 'day') setCurrentDate(addDays(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Global search across all dispatches
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }
    const q = query.trim().toLowerCase();
    const results = dispatches.filter(d => {
      const number = (d.dispatch_number || '').toString().toLowerCase();
      const customer = (d.customer_name || '').toLowerCase();
      const po = (d.po_number || '').toLowerCase();
      return number.includes(q) || customer.includes(q) || po.includes(q);
    });
    setSearchResults(results);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const goToOrderDate = (order) => {
    if (!order.dispatch_date) {
      toast.warning('This order has no dispatch date set');
      return;
    }
    // Try dd/MM/yyyy first, then yyyy-MM-dd
    let date = parse(order.dispatch_date, 'dd/MM/yyyy', new Date());
    if (isNaN(date.getTime())) {
      date = new Date(order.dispatch_date);
    }
    if (isNaN(date.getTime())) {
      toast.error('Could not parse dispatch date');
      return;
    }
    setCurrentDate(date);
    setViewMode('day');
    setSearchQuery('');
    setSearchResults(null);
  };

  const getNextStatus = (currentStatus) => {
    const workflow = ['draft', 'in_progress', 'locked', 'dispatched'];
    const currentIndex = workflow.indexOf(currentStatus);
    if (currentIndex < workflow.length - 1) {
      return workflow[currentIndex + 1];
    }
    return null;
  };

  const getPrevStatus = (currentStatus) => {
    const workflow = ['draft', 'in_progress', 'locked', 'dispatched'];
    const currentIndex = workflow.indexOf(currentStatus);
    if (currentIndex > 0) {
      return workflow[currentIndex - 1];
    }
    return null;
  };

  // Generate batch code
  // Track sequence counter for batch codes generated today
  const [batchSeqCounter, setBatchSeqCounter] = useState(1);
  
  // Helper functions to remember last use_by_date per product
  const getLastUseByDate = (productId) => {
    try {
      const stored = localStorage.getItem('lastUseByDates');
      if (stored) {
        const dates = JSON.parse(stored);
        return dates[productId] || '';
      }
    } catch (e) {
      console.error('Error reading use_by_date from localStorage:', e);
    }
    return '';
  };
  
  const saveLastUseByDate = (productId, date) => {
    try {
      const stored = localStorage.getItem('lastUseByDates');
      const dates = stored ? JSON.parse(stored) : {};
      dates[productId] = date;
      localStorage.setItem('lastUseByDates', JSON.stringify(dates));
    } catch (e) {
      console.error('Error saving use_by_date to localStorage:', e);
    }
  };
  
  const generateBatchCode = (lineIndex = null) => {
    const now = new Date();
    // Format: YYMMDD-SEQ (e.g., 260129-001)
    const yy = now.getFullYear().toString().slice(-2);
    const mm = (now.getMonth() + 1).toString().padStart(2, '0');
    const dd = now.getDate().toString().padStart(2, '0');
    const dateStr = `${yy}${mm}${dd}`;
    
    // Find existing batch codes for today to determine next sequence
    const todayPrefix = dateStr;
    let maxSeq = 0;
    
    // Check existing batches for today's sequence
    batches.forEach(b => {
      if (b.batch_code && b.batch_code.startsWith(todayPrefix)) {
        const parts = b.batch_code.split('-');
        if (parts.length === 2) {
          const seq = parseInt(parts[1], 10);
          if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
        }
      }
    });
    
    // Also check batch lines currently being created
    batchLines.forEach((line, idx) => {
      if (idx !== lineIndex && line.batch_code && line.batch_code.startsWith(todayPrefix)) {
        const parts = line.batch_code.split('-');
        if (parts.length === 2) {
          const seq = parseInt(parts[1], 10);
          if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
        }
      }
    });
    
    const nextSeq = Math.max(maxSeq + 1, batchSeqCounter);
    setBatchSeqCounter(nextSeq + 1);
    
    return `${dateStr}-${nextSeq.toString().padStart(3, '0')}`;
  };

  // Check if user can override order protection
  const canOverrideOrderProtection = () => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const perms = user.permissions || {};
    return perms.can_override_order_protection === true;
  };

  // Handle going back in status (with checks)
  const handleStatusGoBack = async (dispatch, targetStatus) => {
    try {
      const result = await checkOrderStatusChange(dispatch.id, targetStatus);
      const checkData = result.data;
      
      // ALWAYS show dialog when going backwards - user requirement
      // This ensures user is always asked about batches/stock
      setPendingRevertDispatch(dispatch);
      setPendingRevertStatus(targetStatus);
      setRevertCheckResult(checkData);
      setBatchAction('delete');  // Default to DELETE - user requirement
      setBatchDeleteReason('Order status reverted');
      setOverrideReason('');
      setRevertDialogOpen(true);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check status change');
    }
  };

  // Execute the status revert
  const handleRevertConfirm = async () => {
    if (!pendingRevertDispatch || !pendingRevertStatus) return;
    
    // Validate override reason if required
    if (revertCheckResult?.requires_override && !overrideReason.trim()) {
      toast.error('Please provide a reason for override');
      return;
    }
    
    // Validate batch delete reason if deleting
    const hasBatches = revertCheckResult?.batches_affected?.length > 0;
    if (hasBatches && batchAction === 'delete' && !batchDeleteReason.trim()) {
      toast.error('Please provide a reason for deleting batches');
      return;
    }
    
    setReverting(true);
    try {
      const payload = {
        target_status: pendingRevertStatus,
        // ALWAYS send batch_action if there are batches
        batch_action: hasBatches ? batchAction : null,
        batch_delete_reason: (hasBatches && batchAction === 'delete') ? batchDeleteReason : null,
        override_reason: revertCheckResult?.requires_override ? overrideReason : null
      };
      
      const result = await revertOrderStatus(pendingRevertDispatch.id, payload);
      
      const actionResult = result.data.batch_action_result || {};
      if (actionResult.deleted?.length > 0) {
        toast.success(`Status reverted. ${actionResult.deleted.length} batch(es) deleted.`);
      } else if (actionResult.kept?.length > 0) {
        toast.success(`Status reverted. ${actionResult.kept.length} batch(es) unlinked but kept in inventory.`);
      } else {
        toast.success(`Status updated to ${pendingRevertStatus.replace('_', ' ')}`);
      }
      
      setRevertDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert status');
    } finally {
      setReverting(false);
    }
  };

  const handleStatusChange = async (dispatch, newStatus) => {
    const currentStatus = dispatch.status || 'draft';
    const workflow = ['draft', 'in_progress', 'locked', 'dispatched'];
    const currentIndex = workflow.indexOf(currentStatus);
    const targetIndex = workflow.indexOf(newStatus);
    const isGoingBack = targetIndex < currentIndex;
    
    // If going backwards, use the special handler
    if (isGoingBack) {
      await handleStatusGoBack(dispatch, newStatus);
      return;
    }
    
    // If changing to "locked" (Ready), show batch creation dialog for ALL products
    if (newStatus === 'locked') {
      setPendingReadyDispatch(dispatch);
      // Pre-fill pallet count from sales-order entry; operator can override
      setPalletCountReady(dispatch.num_pallets != null ? String(dispatch.num_pallets) : '');

      // If a saved batch draft exists, restore it verbatim — operator continues where they left off
      const draft = dispatch.batch_draft;
      if (draft && (draft.productBatches || draft.productUseDates)) {
        setProductBatches(draft.productBatches || {});
        setProductUseDates(draft.productUseDates || {});
        setProductNotes(draft.productNotes || {});
        setOutOfStockProducts(draft.outOfStockProducts || {});
        setBatchStep(1);
        setBatchLines([]);
        setReadyDialogOpen(true);
        toast.info(
          `Resumed batch entry from draft${draft.saved_at ? ' (saved ' + draft.saved_at.slice(0, 16).replace('T', ' ') + ')' : ''}`,
          { duration: 4000 }
        );
        return;
      }

      // Initialize product batches - one batch per product by default
      const initialBatches = {};
      const initialUseDates = {};
      (dispatch.lines || []).forEach((line, index) => {
        // Start with one batch per product, qty matching ordered
        initialBatches[index] = [{
          batch_code: '',
          quantity: line.quantity?.toString() || '',
          dom_date: ''
        }];
        // Pre-fill use-by date from localStorage if available
        initialUseDates[index] = getLastUseByDate(line.product_id);
      });
      setProductBatches(initialBatches);
      setProductUseDates(initialUseDates);
      setProductNotes({});
      setBatchStep(1);  // Start at step 1
      setBatchLines([]);
      setReadyDialogOpen(true);
      return;
    }

    // If changing to dispatched and no lines exist, show quick dispatch dialog
    if (newStatus === 'dispatched' && (!dispatch.lines || dispatch.lines.length === 0)) {
      setPendingDispatchId(dispatch.id);
      setDispatchLines([{ 
        product_id: '', 
        batch_id: '', 
        batch_code: '',
        use_by_date: '',
        quantity: '', 
        weight_kg: '', 
        cases: '',
        format: '',
        cobranding_id: '',
        notes: ''
      }]);
      setQuickDispatchOpen(true);
      return;
    }

    // If dispatching, ask for uplift date first
    if (newStatus === 'dispatched') {
      setUpliftDispatchRef(dispatch);
      setUpliftDate(dispatch.dispatch_date || null);
      setUpliftDialogOpen(true);
      return;
    }

    try {
      await updateDispatchStatus(dispatch.id, newStatus);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Update a batch line field
  const updateBatchLine = (index, field, value) => {
    setBatchLines(prev => {
      const updated = prev.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      );
      return updated;
    });
  };
  
  // Add a batch to a product
  const addBatchToProduct = (productIndex) => {
    setProductBatches(prev => ({
      ...prev,
      [productIndex]: [...(prev[productIndex] || []), { batch_code: '', quantity: '', weight_kg: '', cases: '' }]
    }));
  };
  
  // Remove a batch from a product
  const removeBatchFromProduct = (productIndex, batchIndex) => {
    setProductBatches(prev => {
      const batches = [...(prev[productIndex] || [])];
      if (batches.length > 1) {
        batches.splice(batchIndex, 1);
      }
      return { ...prev, [productIndex]: batches };
    });
  };
  
  // Update a specific batch for a product
  const updateProductBatch = (productIndex, batchIndex, field, value) => {
    setProductBatches(prev => {
      const batches = [...(prev[productIndex] || [])];
      batches[batchIndex] = { ...batches[batchIndex], [field]: value };
      return { ...prev, [productIndex]: batches };
    });
  };
  
  // Calculate total quantity entered for a product
  const getTotalBatchQty = (productIndex) => {
    const batches = productBatches[productIndex] || [];
    const total = batches.reduce((sum, b) => sum + (parseFloat(b.quantity) || 0), 0);
    // Fix floating point precision (0.1 + 0.2 = 0.30000000000000004)
    return Math.round(total * 1000) / 1000;
  };
  
  // Validation rules for batch creation
  // Units: Can be 5% higher or 5% lower (soft warning), strict limits beyond
  // Weight: Can be 20% higher or 5% lower (soft warning), strict limits beyond
  const validateBatchValues = () => {
    const lines = pendingReadyDispatch?.lines || [];
    const warnings = [];
    const errors = [];
    
    for (let i = 0; i < lines.length; i++) {
      // Skip validation for out-of-stock products
      if (outOfStockProducts[i]) continue;

      const batches = productBatches[i] || [];
      const productName = lines[i].product_name || 'Product';
      const orderedQty = parseFloat(lines[i].quantity) || 0;
      const orderedWeight = parseFloat(lines[i].estimated_weight || lines[i].total_weight) || 0;
      const orderedCases = parseFloat(lines[i].cases) || 0;
      
      // Calculate totals
      const totalQty = getTotalBatchQty(i);
      const totalWeight = batches.reduce((sum, b) => sum + (parseFloat(b.weight_kg) || 0), 0);
      const totalCases = batches.reduce((sum, b) => sum + (parseFloat(b.cases) || 0), 0);
      
      // Basic validation
      for (const batch of batches) {
        if (!batch.batch_code || batch.batch_code.length < 6 || batch.batch_code.length > 8) {
          errors.push(`${productName}: Batch code must be 6-8 characters`);
        }
        if (!batch.quantity || parseFloat(batch.quantity) <= 0) {
          errors.push(`${productName}: Units must be greater than 0`);
        }
        if (orderedWeight > 0 && (!batch.weight_kg || parseFloat(batch.weight_kg) <= 0)) {
          errors.push(`${productName}: Weight (kg) is required`);
        }
      }
      
      // Units/Quantity validation - SOFT WARNING only (no hard block)
      if (orderedQty > 0) {
        const qtyDiff = totalQty - orderedQty;
        const qtyPercent = (qtyDiff / orderedQty) * 100;
        
        // Show warning if there's any significant deviation
        if (Math.abs(qtyPercent) > 0.1) {
          warnings.push(`${productName}: Units ${totalQty} is ${qtyPercent > 0 ? '+' : ''}${qtyPercent.toFixed(1)}% ${qtyPercent > 0 ? 'above' : 'below'} ordered ${orderedQty}`);
        }
      }
      
      // Cases validation - can be 20% higher or 20% lower (soft warning only, no hard block)
      if (orderedCases > 0 && totalCases > 0) {
        const casesDiff = totalCases - orderedCases;
        const casesPercent = (casesDiff / orderedCases) * 100;
        
        if (Math.abs(casesPercent) > 0.1) {
          warnings.push(`${productName}: Cases ${totalCases.toFixed(1)} is ${casesPercent > 0 ? '+' : ''}${casesPercent.toFixed(1)}% ${casesPercent > 0 ? 'above' : 'below'} ordered ${orderedCases.toFixed(1)}`);
        }
      }
      
      // Weight validation - SOFT WARNING only (no hard block)
      if (orderedWeight > 0) {
        const weightDiff = totalWeight - orderedWeight;
        const weightPercent = (weightDiff / orderedWeight) * 100;
        
        // Show warning if there's any significant deviation
        if (Math.abs(weightPercent) > 0.1) {
          warnings.push(`${productName}: Weight ${totalWeight.toFixed(2)} kg is ${weightPercent > 0 ? '+' : ''}${weightPercent.toFixed(1)}% ${weightPercent > 0 ? 'above' : 'below'} ordered ${orderedWeight.toFixed(2)} kg`);
        }
      }
    }
    
    return { errors, warnings, isValid: errors.length === 0 };
  };
  
  // Check if all batch codes are valid (6+ chars) - basic check for button enable
  // Note: Units and weight variations are SOFT WARNINGS only, not hard blocks
  const validateBatchCodes = () => {
    const lines = pendingReadyDispatch?.lines || [];
    for (let i = 0; i < lines.length; i++) {
      // Skip validation for out-of-stock products
      if (outOfStockProducts[i]) continue;

      const batches = productBatches[i] || [];
      const orderedWeight = parseFloat(lines[i].estimated_weight || lines[i].total_weight) || 0;
      
      for (const batch of batches) {
        // Hard block: batch code must be 6-8 characters
        if (!batch.batch_code || batch.batch_code.length < 6 || batch.batch_code.length > 8) {
          return false;
        }
        // Hard block: quantity must be greater than 0
        if (!batch.quantity || parseFloat(batch.quantity) <= 0) {
          return false;
        }
        // Hard block: weight must be entered if ordered weight exists
        if (orderedWeight > 0 && (!batch.weight_kg || parseFloat(batch.weight_kg) <= 0)) {
          return false;
        }
      }
      
      // Units and weight variations are SOFT WARNINGS only - do not block here
      // The validateBatchValues() function will show warnings but won't prevent submission
    }
    return true;
  };
  
  // Check if all use-by dates are entered (or marked as "No Date")
  const validateUseDates = () => {
    const lines = pendingReadyDispatch?.lines || [];
    for (let i = 0; i < lines.length; i++) {
      // Skip validation for out-of-stock products
      if (outOfStockProducts[i]) continue;

      const productDate = productUseDates[i];
      if (!productDate) return false;
      // Also validate per-batch overrides
      const batches = productBatches[i] || [];
      for (const batch of batches) {
        if (batch.has_own_date && !batch.own_use_by_date && !batch.own_no_date) {
          return false;
        }
      }
    }
    return true;
  };
  
  // Generate final batch lines from product batches and use dates
  const generateFinalBatchLines = () => {
    if (!pendingReadyDispatch) return;
    
    const dispatch = pendingReadyDispatch;
    const lines = [];
    let batchIndex = 0;
    
    // Validate that all products exist
    const missingProducts = [];
    
    (dispatch.lines || []).forEach((line) => {
      if (!line.product_id) {
        missingProducts.push(line.product_name || 'Unknown Product');
        return;
      }
      
      const product = products.find(p => p.id === line.product_id);
      if (!product) {
        missingProducts.push(line.product_name || line.product_id);
      }
    });
    
    // Show warning for missing products
    if (missingProducts.length > 0) {
      toast.error(`Cannot create batches: Product "${missingProducts.join(', ')}" not found. It may have been deleted.`, { duration: 8000 });
      return;
    }
    
    (dispatch.lines || []).forEach((line, productIndex) => {
      const batches = productBatches[productIndex] || [];
      const useByDate = productUseDates[productIndex] || '';
      const notes = productNotes[productIndex] || line.notes || '';
      const cobranding = line.cobranding_id ? cobrandings.find(c => c.id === line.cobranding_id) : null;
      const isOutOfStock = outOfStockProducts[productIndex] || false;

      // If out of stock, create a single placeholder line
      if (isOutOfStock) {
        lines.push({
          id: batchIndex++,
          line_id: line.id,
          product_id: line.product_id || '',
          product_name: line.product_name || products.find(p => p.id === line.product_id)?.name || '',
          cobranding_id: line.cobranding_id || '',
          cobranding_name: cobranding?.name || '',
          supplier_product_code: line.supplier_product_code || '',
          batch_code: 'N/A',
          quantity: '0',
          weight_kg: '0',
          cases: '0',
          original_quantity: line.quantity?.toString() || '',
          expiry_date: 'No Date',
          create_batch: false,
          is_sub_batch: false,
          batch_number: 1,
          total_batches: 1,
          notes: notes,
          out_of_stock: true,
        });
        return;
      }
      
      // Filter out empty / cancelled batch rows BEFORE numbering.
      // A row is treated as cancelled if the user added it (via the
      // "+ Batch" button) and then cleared everything OR never typed
      // anything in. Without this filter the empty row gets persisted
      // as a phantom line on the dispatch and inflates both the
      // line-items table and the Combined Totals at the bottom of the
      // dispatch note.
      const realBatches = batches.filter(batch => {
        const trimmedCode = (batch.batch_code || '').trim();
        const qtyNum = parseFloat(batch.quantity);
        const casesNum = parseFloat(batch.cases);
        const weightNum = parseFloat(batch.weight_kg);
        return (
          trimmedCode ||
          (Number.isFinite(qtyNum) && qtyNum > 0) ||
          (Number.isFinite(casesNum) && casesNum > 0) ||
          (Number.isFinite(weightNum) && weightNum > 0)
        );
      });

      realBatches.forEach((batch, idx) => {
        // Per-batch use-by date override: if this batch has its own date, use it
        const batchDate = batch.has_own_date && (batch.own_use_by_date || batch.own_no_date)
          ? (batch.own_no_date ? 'No Date' : batch.own_use_by_date)
          : useByDate;
        
        lines.push({
          id: batchIndex++,
          line_id: line.id,
          product_id: line.product_id || '',
          product_name: line.product_name || products.find(p => p.id === line.product_id)?.name || '',
          cobranding_id: line.cobranding_id || '',
          cobranding_name: cobranding?.name || '',
          supplier_product_code: line.supplier_product_code || '',
          batch_code: batch.batch_code,
          quantity: batch.quantity,
          weight_kg: batch.weight_kg || '',
          cases: batch.cases || '',
          dom_date: batch.dom_date || '',
          existing_batch_id: batch.existing_batch_id || null,  // reused in edit mode
          original_quantity: line.quantity?.toString() || '',
          expiry_date: batchDate,
          create_batch: true,
          is_sub_batch: idx > 0,
          batch_number: idx + 1,
          total_batches: realBatches.length,
          notes: notes,
        });
      });
      
      // Save use-by date to localStorage for future use (skip "No Date")
      if (line.product_id && useByDate && useByDate !== 'No Date') {
        saveLastUseByDate(line.product_id, useByDate);
      }
    });
    
    setBatchLines(lines);
    setBatchStep(3); // Move to review step
  };
  
  // Add a sub-batch line for remaining quantity
  const addSubBatchLine = (parentIndex) => {
    setBatchLines(prev => {
      const parentLine = prev[parentIndex];
      const enteredQty = parseFloat(parentLine.quantity) || 0;
      const originalQty = parseFloat(parentLine.original_quantity) || 0;
      const remaining = originalQty - enteredQty;
      
      if (remaining <= 0) {
        toast.error('No remaining quantity to add');
        return prev;
      }
      
      const newLine = {
        id: prev.length,
        parent_index: parentIndex,
        line_id: parentLine.line_id,
        product_id: parentLine.product_id,
        product_name: parentLine.product_name,
        cobranding_id: parentLine.cobranding_id,
        batch_code: generateBatchCode(),
        quantity: remaining.toString(),
        weight_kg: '', // Weight in KG - independent from quantity
        cases: '', // Cases - manual entry
        original_quantity: remaining.toString(),
        expiry_date: '',
        create_batch: true,
        is_sub_batch: true
      };
      
      // Update the parent's remaining to 0
      const updated = prev.map((line, i) => 
        i === parentIndex ? { ...line, remaining: 0 } : line
      );
      
      // Insert the new line after the parent
      const insertIndex = parentIndex + 1;
      return [...updated.slice(0, insertIndex), newLine, ...updated.slice(insertIndex)];
    });
  };
  
  // Remove a sub-batch line
  const removeSubBatchLine = (index) => {
    setBatchLines(prev => {
      const lineToRemove = prev[index];
      if (!lineToRemove.is_sub_batch) {
        toast.error('Cannot remove the main batch line');
        return prev;
      }
      
      const newLines = prev.filter((_, i) => i !== index);
      
      // Recalculate remaining for parent
      const parentIndex = lineToRemove.parent_index;
      if (parentIndex !== undefined && newLines[parentIndex]) {
        const parentLine = newLines[parentIndex];
        const originalQty = parseFloat(parentLine.original_quantity) || 0;
        const relatedLines = newLines.filter(l => 
          l.parent_index === parentIndex || l === parentLine
        );
        const totalEntered = relatedLines.reduce((sum, l) => sum + (parseFloat(l.quantity) || 0), 0);
        newLines[parentIndex] = { ...parentLine, remaining: originalQty - totalEntered };
      }
      
      return newLines;
    });
  };

  // ──────────────────────────────────────────────────────────────────────────
  // EDIT BATCH DATA — re-open the batch entry dialog for an already-Ready /
  // dispatched order so the operator can correct mistakes (e.g. flag a line
  // as Seasonally Out Of Stock that wasn't, fix a wrong batch code or weight).
  // Does NOT change dispatch status and does NOT re-create existing batches.
  // ──────────────────────────────────────────────────────────────────────────
  const openEditBatchData = (dispatch) => {
    if (!dispatch || !dispatch.lines) return;

    // Reverse-engineer the per-product-line state shape used by the dialog.
    // After Ready, each batch was unpacked into its own dispatch line, so multiple
    // lines may share the same product_id + cobranding_id. Group them back.
    const groupKey = (line) => `${line.product_id || ''}::${line.cobranding_id || ''}`;
    const groups = {};
    const orderedKeys = [];
    for (const line of dispatch.lines) {
      const k = groupKey(line);
      if (!groups[k]) {
        groups[k] = { sample: line, lines: [] };
        orderedKeys.push(k);
      }
      groups[k].lines.push(line);
    }

    const productBatchesEdit = {};
    const productUseDatesEdit = {};
    const productNotesEdit = {};
    const outOfStockEdit = {};
    // We need to set pendingReadyDispatch with a "merged" lines array — one entry per product+cobranding,
    // so the dialog renders one card per product (not one per batch).
    const mergedLines = orderedKeys.map((k, idx) => {
      const g = groups[k];
      const sample = g.sample;
      const totalQty = g.lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0);

      if (sample.out_of_stock) {
        outOfStockEdit[idx] = true;
        productBatchesEdit[idx] = [{ batch_code: '', quantity: totalQty.toString(), weight_kg: '', cases: '', dom_date: '' }];
        productUseDatesEdit[idx] = '';
      } else {
        // One row per existing batch
        productBatchesEdit[idx] = g.lines.map(l => ({
          batch_code: l.batch_code || '',
          quantity: l.quantity?.toString() || '',
          weight_kg: (l.weight_override ?? l.total_weight ?? l.estimated_weight ?? '').toString(),
          cases: (l.cases_override ?? l.calculated_cases ?? l.cases ?? '').toString(),
          dom_date: l.dom_date || '',
          existing_batch_id: l.batch_id || null,  // marker so we can reuse instead of creating
          lockedField: null,
        }));
        // Use-by carried from first batch (they should match per product anyway)
        productUseDatesEdit[idx] = sample.use_by_date || '';
      }
      if (sample.notes) productNotesEdit[idx] = sample.notes;

      return {
        ...sample,
        quantity: totalQty,
      };
    });

    setPendingReadyDispatch({ ...dispatch, lines: mergedLines });
    setPalletCountReady(dispatch.num_pallets != null ? String(dispatch.num_pallets) : '');
    setProductBatches(productBatchesEdit);
    setProductUseDates(productUseDatesEdit);
    setProductNotes(productNotesEdit);
    setOutOfStockProducts(outOfStockEdit);
    setEditingBatchData(true);
    setBatchStep(1);
    setBatchLines([]);
    setReadyDialogOpen(true);
    toast.info('Editing batch data — saving will update lines without changing order status.', { duration: 4000 });
  };

  // Save a partial batch-entry draft against the dispatch.
  const handleSaveBatchDraft = async () => {
    if (!pendingReadyDispatch?.id) {
      toast.error('No order selected');
      return;
    }
    setSavingDraft(true);
    try {
      await api.post(`/dispatches/${pendingReadyDispatch.id}/batch-draft`, {
        productBatches,
        productUseDates,
        productNotes,
        outOfStockProducts,
      });
      toast.success('Draft saved. Reopen this order from the Ready button to continue.', { duration: 5000 });
      setReadyDialogOpen(false);
      setBatchStep(1);
      setProductBatches({});
      setProductUseDates({});
      setProductNotes({});
      setOutOfStockProducts({});
      // Refresh dispatches so the row reflects the new batch_draft state
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleReadyWithBatch = async () => {
    // Check if at least one batch will be created
    const linesToCreate = batchLines.filter(line => line.create_batch && line.product_id && line.quantity);
    
    if (linesToCreate.length === 0) {
      toast.error('Please select at least one product to create batches for');
      return;
    }

    // Validate all lines that will be created
    for (const line of linesToCreate) {
      if (!line.batch_code) {
        toast.error(`Please enter a batch code for ${line.product_name || 'all products'}`);
        return;
      }
      if (parseFloat(line.quantity) <= 0) {
        toast.error(`Please enter a valid units count for ${line.product_name || 'all products'}`);
        return;
      }
    }
    
    // Validate that total quantities match original ordered quantities (soft warning only)
    const originalLines = batchLines.filter(l => !l.is_sub_batch);
    for (const origLine of originalLines) {
      const relatedBatches = linesToCreate.filter(l => 
        l.line_id === origLine.line_id || 
        l.parent_index === batchLines.indexOf(origLine) ||
        l === origLine
      );
      const totalQty = relatedBatches.reduce((sum, l) => sum + (parseFloat(l.quantity) || 0), 0);
      const originalQty = parseFloat(origLine.original_quantity) || 0;
      
      // Show warning but allow to proceed (user may intentionally dispatch partial)
      if (Math.abs(totalQty - originalQty) > 0.01) {
        toast.warning(`Note: Total units for ${origLine.product_name} (${totalQty}) differs from ordered units (${originalQty}).`);
        // Don't block - just warn
      }
    }

    setCreatingBatch(true);
    try {
      // Create batches and collect info for dispatch lines
      const createdBatchesInfo = [];
      let batchesCreated = 0;
      
      // Create batches for all selected products
      for (const line of linesToCreate) {
        // Calculate use_by_date
        let useByDate = line.expiry_date;
        if (!useByDate || useByDate === 'No Date') {
          if (useByDate === 'No Date') {
            useByDate = 'No Date';
          } else {
            const defaultExpiry = new Date();
            defaultExpiry.setDate(defaultExpiry.getDate() + 90);
            useByDate = format(defaultExpiry, 'dd/MM/yyyy');
          }
        } else if (useByDate.includes('-') && useByDate.split('-')[0].length === 4) {
          // Convert from YYYY-MM-DD to DD/MM/YYYY only if it's in ISO format
          const parts = useByDate.split('-');
          useByDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        // If already DD/MM/YYYY, keep as-is
        
        // Save the use_by_date for this product to localStorage for future pre-fill
        if (line.product_id && line.expiry_date) {
          saveLastUseByDate(line.product_id, line.expiry_date);
        }

        // Validate product_id before making API call
        if (!line.product_id) {
          throw new Error(`Missing product ID for batch "${line.batch_code}". Please ensure a product is selected.`);
        }

        // Create the batch with weight and cases
        const batchPayload = {
          batch_code: line.batch_code,
          product_id: line.product_id,
          quantity: parseFloat(line.quantity),  // Backend expects 'quantity', not 'quantity_produced'
          weight_kg: line.weight_kg ? parseFloat(line.weight_kg) : null,
          cases: line.cases ? parseFloat(line.cases) : null,
          use_by_date: useByDate
        };
        
        try {
          // EDIT MODE: if this row already had a saved batch with the same code,
          // reuse it instead of creating a duplicate batch record.
          let createdBatch;
          if (editingBatchData && line.existing_batch_id) {
            createdBatch = {
              id: line.existing_batch_id,
              batch_code: line.batch_code,
              use_by_date: useByDate,
            };
          } else {
            const response = await createBatch(batchPayload);
            createdBatch = response.data;
          }
          
          // Store batch info for creating dispatch lines
          createdBatchesInfo.push({
            line_id: line.line_id,
            product_id: line.product_id,
            product_name: line.product_name,
            cobranding_id: line.cobranding_id,
            batch_id: createdBatch.id,
            batch_code: createdBatch.batch_code,
            use_by_date: createdBatch.use_by_date,
            quantity: parseFloat(line.quantity),
            weight_kg: line.weight_kg ? parseFloat(line.weight_kg) : null,
            cases: line.cases ? parseFloat(line.cases) : null,
            dom_date: line.dom_date || '',
            is_sub_batch: line.is_sub_batch,
            notes: line.notes || '',
          });
          batchesCreated++;
        } catch (batchError) {
          // Enhanced error message with product context
          const detail = batchError.response?.data?.detail || batchError.message;
          throw new Error(`Failed to create batch for "${line.product_name || 'Unknown Product'}": ${detail}`);
        }
      }

      toast.success(`Created ${batchesCreated} batch${batchesCreated > 1 ? 'es' : ''} for this order`);

      // Build updated dispatch lines - each batch becomes a separate dispatch line
      const updatedLines = [];
      
      // Group batch info by original line_id
      const batchesByLineId = {};
      createdBatchesInfo.forEach(info => {
        const key = info.line_id || info.product_id;
        if (!batchesByLineId[key]) {
          batchesByLineId[key] = [];
        }
        batchesByLineId[key].push(info);
      });
      
      // Create dispatch lines - multiple lines per product if multiple batches
      for (const dispatchLine of pendingReadyDispatch.lines) {
        const batchesForLine = batchesByLineId[dispatchLine.id] || batchesByLineId[dispatchLine.product_id] || [];
        
        if (batchesForLine.length === 0) {
          // No batch was created for this line. Two reasons:
          //  (a) Operator marked it as "Out Of Stock" via the OOS checkbox — must persist the OOS flag
          //  (b) Edge case where validation passed but no batch was actually entered
          const isOOS = !!outOfStockProducts[pendingReadyDispatch.lines.indexOf(dispatchLine)];
          updatedLines.push({
            product_id: dispatchLine.product_id,
            product_name: dispatchLine.product_name,
            supplier_product_code: dispatchLine.supplier_product_code,
            quantity: dispatchLine.quantity,
            unit_type: dispatchLine.unit_type || 'units',
            notes: dispatchLine.notes,
            cobranding_id: dispatchLine.cobranding_id,
            cobranding_name: dispatchLine.cobranding_name,
            batch_code: isOOS ? null : (dispatchLine.batch_code || null),
            use_by_date: isOOS ? null : (dispatchLine.use_by_date || null),
            // OOS lines MUST NOT contribute to totals — null overrides so compute_dispatch_totals skips them
            weight_override: isOOS ? null : (dispatchLine.weight_override || dispatchLine.total_weight || null),
            cases_override: isOOS ? null : (dispatchLine.cases_override || dispatchLine.calculated_cases || null),
            estimated_weight: isOOS ? null : dispatchLine.estimated_weight,
            out_of_stock: isOOS,
          });
        } else {
          // Create a dispatch line for each batch
          for (const batchInfo of batchesForLine) {
            updatedLines.push({
              product_id: batchInfo.product_id,
              product_name: dispatchLine.product_name,
              quantity: batchInfo.quantity,
              unit_type: 'units',
              notes: batchInfo.notes || dispatchLine.notes,
              cobranding_id: batchInfo.cobranding_id || dispatchLine.cobranding_id,
              supplier_product_code: dispatchLine.supplier_product_code,  // Preserve supplier code
              batch_id: batchInfo.batch_id,
              batch_code: batchInfo.batch_code,
              use_by_date: batchInfo.use_by_date,
              dom_date: batchInfo.dom_date || null,
              weight_override: batchInfo.weight_kg ? parseFloat(batchInfo.weight_kg) : null,
              cases_override: batchInfo.cases ? parseFloat(batchInfo.cases) : null
            });
          }
        }
      }

      // Update dispatch with batch-linked lines + actual pallet count (overrides SO estimate)
      const dispatchUpdate = { lines: updatedLines };
      const parsedPallets = palletCountReady.trim() === '' ? null : parseInt(palletCountReady, 10);
      if (parsedPallets !== null && !Number.isNaN(parsedPallets) && parsedPallets >= 0) {
        dispatchUpdate.num_pallets = parsedPallets;
      }
      const { data: savedOrder } = await updateDispatch(pendingReadyDispatch.id, dispatchUpdate);
      announceAdjustments(toast, savedOrder?.phased_adjustments, savedOrder?.phased_adjust_failed);

      if (editingBatchData) {
        // Edit mode — preserve current status, do NOT touch draft, do NOT mark Ready.
        toast.success('Batch data updated');
      } else {
        // First-time Ready — promote status and clear any draft
        await updateDispatchStatus(pendingReadyDispatch.id, 'locked');
        toast.success('Order marked as Ready');
        try {
          await api.delete(`/dispatches/${pendingReadyDispatch.id}/batch-draft`);
        } catch { /* non-blocking */ }
      }

      setReadyDialogOpen(false);
      setEditingBatchData(false);
      loadData();
    } catch (error) {
      // Handle both API errors and custom thrown errors
      let errorMsg = 'Failed to create batch';
      
      if (error.message && !error.response) {
        // Custom thrown error (from our validation or enhanced error handling)
        errorMsg = error.message;
      } else {
        // API error
        const errorDetail = error.response?.data?.detail;
        if (typeof errorDetail === 'string') {
          errorMsg = errorDetail;
        } else if (Array.isArray(errorDetail)) {
          errorMsg = errorDetail.map(e => e.msg || e).join(', ');
        }
      }
      
      toast.error(errorMsg, { duration: 6000 });
    } finally {
      setCreatingBatch(false);
    }
  };

  const getProductNames = (dispatch) => {
    if (!dispatch.lines || dispatch.lines.length === 0) return '-';
    const names = dispatch.lines.map(l => l.product_name).filter(Boolean);
    if (names.length === 0) return '-';
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  };

  // View/Edit Order handlers
  const openViewOrderDialog = (order) => {
    setSelectedOrder(order);
    setOrderFormData({
      customer_id: order.customer_id || '',
      po_number: order.po_number || '',
      po_date: order.po_date || '',
      dispatch_date: order.dispatch_date || '',
      vehicle_info: order.vehicle_info || '',
      notes: order.notes || '',
      lines: order.lines || [],
      // Delivery destination context — carried so the edit dropdown can change it
      delivery_address_id: order.delivery_address_id || null,
      delivery_address: order.delivery_address || null,
      selected_delivery_address: order.selected_delivery_address || null,
      delivery_contact_name: order.delivery_contact_name || null,
      delivery_contact_phone: order.delivery_contact_phone || null,
      delivery_instructions: order.delivery_instructions || null,
    });
    // Can edit if draft or in_progress
    const canEditOrder = ['draft', 'in_progress'].includes(order.status);
    setEditingOrder(false); // Start in view mode
    setViewOrderDialogOpen(true);
  };

  const canEditOrderFully = (order) => {
    // Can fully edit (including lines) if draft or in_progress
    return ['draft', 'in_progress'].includes(order?.status);
  };

  // ── Phased dispatch handlers ──
  const openPartById = async (part) => {
    const local = dispatches.find(d => d.id === part.id);
    if (local) { openViewOrderDialog(local); return; }
    try {
      const { data } = await getDispatch(part.id);
      openViewOrderDialog(data);
    } catch (error) {
      toast.error('Could not open that order');
    }
  };

  const openSplitFromReady = () => {
    const d = pendingReadyDispatch;
    const prefill = Object.fromEntries((d?.lines || []).map((_, i) => [i, getTotalBatchQty(i)]));
    setReadyDialogOpen(false);
    setSplitTarget({ dispatch: d, prefill, fromReady: true });
  };

  const closeSplit = () => {
    if (splitTarget?.fromReady) setReadyDialogOpen(true);
    setSplitTarget(null);
  };

  const handleSplitDone = async (result) => {
    const target = splitTarget;
    setSplitTarget(null);
    setViewOrderDialogOpen(false);
    toast.success(`Order #${result.balance.dispatch_number} created as Part ${result.balance.part_number} for ${result.balance.dispatch_date}`);
    if (target?.fromReady) {
      const kept = result.kept_indexes || [];
      const remap = (obj) => Object.fromEntries(kept.map((old, i) => [i, obj?.[old]]).filter(([, v]) => v !== undefined));
      setProductBatches(prev => remap(prev));
      setProductUseDates(prev => remap(prev));
      setProductNotes(prev => remap(prev));
      setOutOfStockProducts(prev => remap(prev));
      try {
        const { data } = await getDispatch(target.dispatch.id);
        setPendingReadyDispatch(data);
        setBatchStep(1);
        setReadyDialogOpen(true);
      } catch (error) {
        toast.error('Split saved — reopen Ready to continue');
      }
    }
    loadData();
  };

  const canEditOrderHeader = (order) => {
    // Can edit header fields (customer, PO, dates, notes) even after batches created
    // But not if dispatched (unless has override permission)
    if (!order) return false;
    if (order.status === 'dispatched') {
      return canOverrideOrderProtection();
    }
    return true;
  };

  const handleSaveOrder = async () => {
    if (!selectedOrder) return;
    
    setSavingOrder(true);
    try {
      const payload = {
        customer_id: orderFormData.customer_id,
        po_number: orderFormData.po_number,
        po_date: orderFormData.po_date,
        dispatch_date: orderFormData.dispatch_date,
        vehicle_info: orderFormData.vehicle_info,
        notes: orderFormData.notes,
        // Delivery destination — only included if changed during edit
        delivery_address_id: orderFormData.delivery_address_id,
        delivery_address: orderFormData.delivery_address,
        selected_delivery_address: orderFormData.selected_delivery_address,
        delivery_contact_name: orderFormData.delivery_contact_name,
        delivery_contact_phone: orderFormData.delivery_contact_phone,
        delivery_instructions: orderFormData.delivery_instructions,
      };
      
      // Only include lines if we can edit them
      if (canEditOrderFully(selectedOrder)) {
        payload.lines = orderFormData.lines;
      }
      
      const { data: savedOrder } = await updateDispatch(selectedOrder.id, payload);
      announceAdjustments(toast, savedOrder?.phased_adjustments, savedOrder?.phased_adjust_failed);
      toast.success('Order updated successfully');
      setViewOrderDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setSavingOrder(false);
    }
  };

  const addOrderLine = () => {
    setOrderFormData({
      ...orderFormData,
      lines: [...orderFormData.lines, { product_id: '', quantity: '', notes: '' }]
    });
  };

  const updateOrderLine = (index, field, value) => {
    const newLines = [...orderFormData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setOrderFormData({ ...orderFormData, lines: newLines });
  };

  const removeOrderLine = (index) => {
    if (orderFormData.lines.length > 1) {
      setOrderFormData({
        ...orderFormData,
        lines: orderFormData.lines.filter((_, i) => i !== index)
      });
    }
  };

  const openCreateDialog = () => {
    setSelectedDate(currentDate);
    setFormData({
      customer_id: '',
      product_id: '',
      cobranding_id: '',
      quantity: '',
      estimated_weight: '',
      po_number: '',
      dispatch_date: format(currentDate, 'dd/MM/yyyy'),
      transport_agent: '',
      notes: ''
    });
    setShowCreateCustomer(false);
    setShowCreateProduct(false);
    setShowCreateCoBranding(false);
    setCreateDialogOpen(true);
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setFormData({ ...formData, dispatch_date: format(date, 'dd/MM/yyyy') });
    }
  };

  // Inline customer creation
  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    setCreatingCustomer(true);
    try {
      const res = await createCustomer({ name: newCustomerName.trim() });
      toast.success(`Customer "${newCustomerName}" created`);
      setCustomers([...customers, res.data]);
      setFormData({ ...formData, customer_id: res.data.id });
      setShowCreateCustomer(false);
      setNewCustomerName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Inline product creation
  const handleCreateProduct = async () => {
    if (!newProductName.trim()) {
      toast.error('Product name is required');
      return;
    }

    setCreatingProduct(true);
    try {
      const res = await createProduct({ 
        name: newProductName.trim(),
        weight_per_unit: parseFloat(newProductWeight) || 1,
        units_per_case: parseInt(newProductUnits) || 1
      });
      toast.success(`Product "${newProductName}" created`);
      setProducts([...products, res.data]);
      setFormData({ ...formData, product_id: res.data.id });
      setShowCreateProduct(false);
      setNewProductName('');
      setNewProductWeight('');
      setNewProductUnits('1');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setCreatingProduct(false);
    }
  };

  // Inline co-branding creation
  const handleCreateCoBranding = async () => {
    if (!newCoBrandingName.trim()) {
      toast.error('Co-branding name is required');
      return;
    }

    setCreatingCoBranding(true);
    try {
      const res = await createCoBranding({ name: newCoBrandingName.trim() });
      toast.success(`Co-branding "${newCoBrandingName}" created`);
      setCobrandings([...cobrandings, res.data]);
      setFormData({ ...formData, cobranding_id: res.data.id });
      setShowCreateCoBranding(false);
      setNewCoBrandingName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create co-branding');
    } finally {
      setCreatingCoBranding(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.customer_id || !formData.dispatch_date) {
      toast.error('Customer and production date are required');
      return;
    }

    setSaving(true);
    try {
      // Build lines if product and quantity provided
      const lines = [];
      if (formData.product_id && formData.quantity) {
        lines.push({
          product_id: formData.product_id,
          quantity: parseFloat(formData.quantity),
          weight_override: formData.estimated_weight ? parseFloat(formData.estimated_weight) : null
        });
      }

      await createDispatch({
        customer_id: formData.customer_id,
        cobranding_id: formData.cobranding_id || null,
        estimated_weight: formData.estimated_weight ? parseFloat(formData.estimated_weight) : null,
        po_number: formData.po_number,
        dispatch_date: formData.dispatch_date,
        vehicle_info: formData.transport_agent,
        notes: formData.notes,
        lines: lines
      });
      toast.success('Order planned');
      setCreateDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  // Quick dispatch handlers
  const addDispatchLine = () => {
    setDispatchLines([...dispatchLines, { 
      product_id: '', 
      batch_id: '', 
      batch_code: '',
      use_by_date: '',
      quantity: '', 
      weight_kg: '', 
      cases: '',
      format: '',
      cobranding_id: '',
      notes: ''
    }]);
  };

  const updateDispatchLine = (index, field, value) => {
    const updated = [...dispatchLines];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-select batch when product changes
    if (field === 'product_id') {
      updated[index].batch_id = '';
    }
    
    setDispatchLines(updated);
  };

  const removeDispatchLine = (index) => {
    if (dispatchLines.length > 1) {
      setDispatchLines(dispatchLines.filter((_, i) => i !== index));
    }
  };

  const getBatchesForProduct = (productId) => {
    return batches.filter(b => b.product_id === productId && b.quantity_available > 0);
  };

  const handleQuickDispatch = async () => {
    const validLines = dispatchLines.filter(l => l.product_id && l.quantity);
    if (validLines.length === 0) {
      toast.error('Please add at least one product line with quantity');
      return;
    }

    setSaving(true);
    try {
      // First update the dispatch with lines
      const dispatch = dispatches.find(d => d.id === pendingDispatchId);
      if (dispatch) {
        // Get product and batch details for each line
        const linesWithDetails = validLines.map(l => {
          const product = products.find(p => p.id === l.product_id);
          const batch = l.batch_id ? batches.find(b => b.id === l.batch_id) : null;
          const cobranding = l.cobranding_id ? cobrandings.find(c => c.id === l.cobranding_id) : null;
          
          return {
            product_id: l.product_id,
            batch_id: l.batch_id || null,
            quantity: parseFloat(l.quantity),
            unit_type: 'units',
            weight_override: l.weight_kg ? parseFloat(l.weight_kg) : null,
            cases_override: l.cases ? parseFloat(l.cases) : null,
            batch_code: l.batch_code || batch?.batch_code || null,
            use_by_date: l.use_by_date || batch?.use_by_date || null,
            cobranding_id: l.cobranding_id || null,
            notes: l.notes || null
          };
        });
        
        await createDispatch({
          ...dispatch,
          id: undefined,
          lines: linesWithDetails
        });
      }
      
      // Then show uplift date dialog before dispatching
      const dispatchRef = dispatches.find(d => d.id === pendingDispatchId) || { id: pendingDispatchId };
      setUpliftDispatchRef(dispatchRef);
      setUpliftDate(dispatchRef.dispatch_date || null);
      setQuickDispatchOpen(false);
      setUpliftDialogOpen(true);
      
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete dispatch');
    } finally {
      setSaving(false);
    }
  };

  // === Email on Dispatch Handlers ===
  const handleEmailPromptYes = async () => {
    setEmailPromptOpen(false);
    if (!emailPromptDispatch) return;
    try {
      const res = await getDispatchEmailTemplate(emailPromptDispatch.id);
      const t = res.data;
      setEmailData({
        to_email: t.to_email || '',
        cc_emails: [],
        subject: t.subject || '',
        body: t.body || '',
        attach_pdf: true
      });
      setEmailCcInput('');
      setEmailComposeOpen(true);
    } catch {
      toast.error('Failed to load email template');
    }
  };

  const handleEmailPromptNo = () => {
    setEmailPromptOpen(false);
    setEmailPromptDispatch(null);
  };

  const handleAddEmailCc = () => {
    const email = emailCcInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (!emailData.cc_emails.includes(email)) {
        setEmailData(prev => ({ ...prev, cc_emails: [...prev.cc_emails, email] }));
      }
      setEmailCcInput('');
    } else if (email) {
      toast.error('Invalid email address');
    }
  };

  const handleRemoveEmailCc = (email) => {
    setEmailData(prev => ({ ...prev, cc_emails: prev.cc_emails.filter(e => e !== email) }));
  };

  const handleSendDispatchEmail = async () => {
    if (!emailData.to_email || !emailData.subject) {
      toast.error('Recipient and subject are required');
      return;
    }
    setSendingEmail(true);
    try {
      const sender = emailDocType === 'coa' ? sendCOAEmail
        : emailDocType === 'pallet_label' ? sendPalletLabelEmail
        : sendDispatchEmail;
      await sender(emailPromptDispatch.id, emailData);
      const labels = { coa: 'COA', pallet_label: 'Pallet label', dispatch_note: 'Dispatch note' };
      toast.success(`${labels[emailDocType]} emailed successfully!`);
      setEmailComposeOpen(false);
      setEmailPromptDispatch(null);
      setEmailDocType('dispatch_note');  // reset to default
      setEmailRecipientSuggestions([]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  /**
   * Open the compose dialog for any of the 3 supported document types directly
   * from the Order Pipeline 3-dots menu. Pre-fills To/Subject/Body using the
   * dispatch's customer (reuses /email-template for dispatch notes; for COA &
   * pallet labels we synthesise a sensible default subject + short body).
   */
  const openEmailCompose = async (dispatch, docType) => {
    setEmailPromptDispatch(dispatch);
    setEmailDocType(docType);
    setEmailRecipientSuggestions([]);
    // Fetch auto-learned recipients in parallel with the template (non-blocking).
    if (dispatch?.customer_id) {
      getCustomerEmailRecipients(dispatch.customer_id, docType)
        .then((res) => setEmailRecipientSuggestions(res.data?.recipients || []))
        .catch(() => { /* non-fatal — chips just won't render */ });
    }
    try {
      // Always pull the dispatch-note template to harvest customer email + greeting.
      let to = '';
      let subject = '';
      let body = '';
      try {
        const res = await getDispatchEmailTemplate(dispatch.id);
        to = res.data?.to_email || '';
        if (docType === 'dispatch_note') {
          subject = res.data?.subject || `Dispatch Note ${dispatch.dispatch_number}`;
          body = res.data?.body || '';
        }
      } catch {
        // continue with synthesised defaults — customer email may be blank
      }
      const dispNum = dispatch.dispatch_number || '';
      const custName = dispatch.customer_name || 'Customer';
      if (docType === 'coa') {
        subject = subject || `Certificate of Analysis Conformance — ${dispNum}`;
        body = `Dear ${custName},\n\nPlease find attached the Certificate of Analysis Conformance for dispatch ${dispNum}.\n\nIf you have any questions, please don't hesitate to contact us.\n\nBest regards`;
      } else if (docType === 'pallet_label') {
        subject = subject || `Pallet Label — ${dispNum}`;
        body = `Dear ${custName},\n\nPlease find attached the pallet label(s) for dispatch ${dispNum}.\n\nBest regards`;
      }
      setEmailData({
        to_email: to,
        cc_emails: [],
        subject,
        body,
        attach_pdf: true,
      });
      setEmailCcInput('');
      setEmailComposeOpen(true);
    } catch (e) {
      toast.error('Failed to load email template');
    }
  };

  // Effective totals helpers — production values where available, ordered as fallback
  // (mirrors backend compute_dispatch_totals)
  const dispCases = (d) => (d.total_cases || d.total_ordered_cases || 0);
  const dispWeight = (d) => (d.total_net_weight || d.total_ordered_weight || 0);

  // Stats for the current view
  const stats = useMemo(() => {
    return {
      total: filteredDispatches.length,
      notStarted: filteredDispatches.filter(d => d.status === 'draft').length,
      ready: filteredDispatches.filter(d => d.status === 'locked').length,
      dispatched: filteredDispatches.filter(d => d.status === 'dispatched').length,
      unscheduled: unscheduledDispatches.length,
      totalUnits: filteredDispatches.reduce((sum, d) => sum + dispUnits(d), 0),
      totalCases: filteredDispatches.reduce((sum, d) => sum + dispCases(d), 0),
      totalWeight: filteredDispatches.reduce((sum, d) => sum + dispWeight(d), 0)
    };
  }, [filteredDispatches, unscheduledDispatches]);

  // Export to PDF
  const handleExportPDF = () => {
    const viewTitle = viewMode === 'day' 
      ? format(currentDate, 'dd MMMM yyyy')
      : viewMode === 'week' 
        ? `Week ${weekNumber}, ${year}`
        : monthName;

    const printContent = `
      <html>
        <head>
          <title>Order Planner - ${viewTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 24px; margin-bottom: 5px; }
            .subtitle { color: #666; margin-bottom: 20px; }
            .stats { display: flex; gap: 20px; margin-bottom: 20px; padding: 10px; background: #f5f5f5; border-radius: 8px; }
            .stat { text-align: center; }
            .stat-value { font-size: 24px; font-weight: bold; }
            .stat-label { font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f5f5f5; font-size: 12px; text-transform: uppercase; }
            .status { padding: 2px 8px; border-radius: 12px; font-size: 11px; }
            .status-draft { background: #fee2e2; color: #b91c1c; }
            .status-locked { background: #dbeafe; color: #1d4ed8; }
            .status-dispatched { background: #dcfce7; color: #15803d; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Cashel Blue - Order Planner</h1>
          <div class="subtitle">${viewTitle}</div>
          <div class="stats">
            <div class="stat"><div class="stat-value">${stats.total}</div><div class="stat-label">Total Orders</div></div>
            <div class="stat"><div class="stat-value">${stats.notStarted}</div><div class="stat-label">Not Started</div></div>
            <div class="stat"><div class="stat-value">${stats.ready}</div><div class="stat-label">Ready</div></div>
            <div class="stat"><div class="stat-value">${stats.dispatched}</div><div class="stat-label">Dispatched</div></div>
            <div class="stat"><div class="stat-value">${fmtUnits(stats.totalUnits)}</div><div class="stat-label">Units</div></div>
            <div class="stat"><div class="stat-value">${stats.totalCases.toFixed(0)}</div><div class="stat-label">Cases</div></div>
            <div class="stat"><div class="stat-value">${stats.totalWeight.toFixed(0)} kg</div><div class="stat-label">Weight</div></div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Prod. Date</th>
                <th>Customer</th>
                <th>Products</th>
                <th>PO #</th>
                <th>Units</th>
                <th>Cases</th>
                <th>Weight</th>
                <th>Transport</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredDispatches.map(d => `
                <tr>
                  <td>${showDate(d.dispatch_date)}</td>
                  <td>${d.customer_name}</td>
                  <td>${getProductNames(d)}</td>
                  <td>${d.po_number || '-'}</td>
                  <td><b>${fmtUnits(dispUnits(d))}</b></td>
                  <td>${dispCases(d).toFixed(1)}</td>
                  <td>${dispWeight(d).toFixed(1)} kg</td>
                  <td>${d.vehicle_info || '-'}</td>
                  <td><span class="status status-${d.status}">${getStatusInfo(d.status).label}</span></td>
                </tr>
              `).join('')}
              ${filteredDispatches.length === 0 ? '<tr><td colspan="9" style="text-align:center;padding:20px;">No orders for this period</td></tr>' : ''}
            </tbody>
          </table>
          <div style="margin-top:20px;font-size:11px;color:#999;">
            Generated on ${format(new Date(), 'dd/MM/yyyy HH:mm')}
          </div>
        </body>
      </html>
    `;

    openPrintWindow(printContent);
  };

  const getViewTitle = () => {
    if (viewMode === 'day') return format(currentDate, 'EEEE, dd MMMM yyyy');
    if (viewMode === 'week') return `Week ${weekNumber}, ${year} (${format(dateRange.start, 'dd MMM')} - ${format(dateRange.end, 'dd MMM')})`;
    return monthName;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="weekly-planner-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Order Pipeline
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and manage orders by day, week, or month
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPDF} data-testid="btn-export-pdf">
            <Printer className="w-4 h-4 mr-2" />
            Print / PDF
          </Button>
          {canEdit(user?.role) && (
            <Button onClick={() => navigate('/sales-order/new')} data-testid="btn-new-sales-order">
              <Plus className="w-4 h-4 mr-2" />
              New Sales Order
            </Button>
          )}
        </div>
      </div>

      {/* Global Search Bar */}
      <Card data-testid="order-search-bar">
        <CardContent className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by dispatch number, customer name, or PO number..."
              className="pl-9 pr-9 h-10"
              data-testid="order-search-input"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted"
                data-testid="order-search-clear"
              >
                <XIcon className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
          {searchResults !== null && (
            <div className="mt-2 text-xs text-muted-foreground flex items-center justify-between">
              <span>{searchResults.length} order{searchResults.length !== 1 ? 's' : ''} found across all dates</span>
              <Button variant="ghost" size="sm" onClick={clearSearch} className="h-6 text-xs">
                Back to calendar view
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Mode Toggle + Navigation — hidden during search */}
      {searchResults === null && (
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              {VIEW_MODES.map(mode => {
                const Icon = mode.icon;
                return (
                  <Button
                    key={mode.value}
                    variant={viewMode === mode.value ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode(mode.value)}
                    className="gap-1"
                  >
                    <Icon className="w-4 h-4" />
                    {mode.label}
                  </Button>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToPrevious}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="text-center min-w-[200px]">
                <div className="font-semibold">{getViewTitle()}</div>
              </div>
              
              <Button variant="outline" size="sm" onClick={goToNext}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <Button variant="link" size="sm" onClick={goToToday} className="text-xs">
                Today
              </Button>
            </div>
          </div>
          {/* Bulk Invoiced controls — operates on currently filtered/visible dispatches */}
          {(() => {
            const eligibleVisible = filteredDispatches.filter(
              (d) => ['ready', 'locked', 'dispatched'].includes(d.status),
            );
            const invoicedCount = eligibleVisible.filter((d) => d.invoiced).length;
            const notInvoicedCount = eligibleVisible.length - invoicedCount;
            if (eligibleVisible.length === 0) return null;
            return (
              <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground" data-testid="bulk-invoice-status">
                  <span className="font-semibold text-emerald-700">{invoicedCount}</span> invoiced ·
                  <span className="font-semibold text-zinc-700 ml-1">{notInvoicedCount}</span> pending
                  <span className="ml-1 text-muted-foreground">out of {eligibleVisible.length} eligible visible</span>
                </span>
                <div className="flex-1" />
                {notInvoicedCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkInvoiced(true)}
                    disabled={bulkInvoicingBusy}
                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    data-testid="btn-bulk-mark-invoiced"
                  >
                    {bulkInvoicingBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                    Mark {notInvoicedCount} visible as Invoiced
                  </Button>
                )}
                {invoicedCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkInvoiced(false)}
                    disabled={bulkInvoicingBusy}
                    className="text-zinc-700"
                    data-testid="btn-bulk-mark-not-invoiced"
                  >
                    {bulkInvoicingBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Circle className="w-3.5 h-3.5 mr-1" />}
                    Un-mark {invoicedCount}
                  </Button>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>
      )}

      {/* === SEARCH RESULTS VIEW === */}
      {searchResults !== null ? (
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold text-sm text-muted-foreground mb-3 uppercase tracking-wide">
              Search Results
            </h2>
            {searchResults.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No orders found matching "{searchQuery}"</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="search-results-table">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-2 font-medium text-xs">Dispatch #</th>
                      <th className="text-left p-2 font-medium text-xs">Customer</th>
                      <th className="text-left p-2 font-medium text-xs">PO #</th>
                      <th className="text-left p-2 font-medium text-xs">Dispatch Date</th>
                      <th className="text-left p-2 font-medium text-xs">Products</th>
                      <th className="text-left p-2 font-medium text-xs">Status</th>
                      <th className="text-right p-2 font-medium text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(d => {
                      const statusInfo = getStatusInfo(d.status);
                      const productNames = (d.lines || []).map(l => l.product_name).filter(Boolean).join(', ');
                      return (
                        <tr key={d.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => openViewOrderDialog(d)} data-testid={`search-result-${d.dispatch_number}`}>
                          <td className="p-2 font-mono font-semibold">{d.dispatch_number}</td>
                          <td className="p-2">{d.customer_name}</td>
                          <td className="p-2 text-muted-foreground">{d.po_number || '—'}</td>
                          <td className="p-2">{d.dispatch_date || 'Unscheduled'}</td>
                          <td className="p-2 text-xs text-muted-foreground max-w-[200px] truncate">{productNames || '—'}</td>
                          <td className="p-2">
                            <Badge variant="outline" className={`text-xs ${statusInfo?.color || ''}`}>{statusInfo?.label || d.status}</Badge>
                          </td>
                          <td className="p-2 text-right flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" title="View order" onClick={(e) => { e.stopPropagation(); openViewOrderDialog(d); }} data-testid={`search-view-${d.dispatch_number}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {d.dispatch_date && (
                              <Button variant="outline" size="sm" title="Go to this date in pipeline" onClick={(e) => { e.stopPropagation(); goToOrderDate(d); }} data-testid={`search-goto-${d.dispatch_number}`} className="text-xs gap-1">
                                <ExternalLink className="w-3.5 h-3.5" />
                                Go to Date
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
      <>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total Orders</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-red-700">{stats.notStarted}</div>
            <div className="text-xs text-red-600">Not Started</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{stats.ready}</div>
            <div className="text-xs text-blue-600">Ready</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-emerald-700">{stats.dispatched}</div>
            <div className="text-xs text-emerald-600">Dispatched</div>
          </CardContent>
        </Card>
        <Card className="bg-[#005F73]/5" data-testid="planner-total-units">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-[#005F73]">{fmtUnits(stats.totalUnits)}</div>
            <div className="text-xs text-muted-foreground">Total Units</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold">{stats.totalCases.toFixed(0)}</div>
            <div className="text-xs text-muted-foreground">Total Cases</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold">{stats.totalWeight.toFixed(0)}</div>
            <div className="text-xs text-muted-foreground">Total kg</div>
          </CardContent>
        </Card>
      </div>

      {/* Unscheduled Orders Banner */}
      {unscheduledDispatches.length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="unscheduled-orders-banner">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-4 h-4" />
              {unscheduledDispatches.length} Unscheduled Order{unscheduledDispatches.length > 1 ? 's' : ''} (No Dispatch Date)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-amber-100/50">
                    <th className="text-left px-4 py-2 text-xs font-medium uppercase text-amber-800">Order #</th>
                    <th className="text-left px-4 py-2 text-xs font-medium uppercase text-amber-800">Customer</th>
                    <th className="text-left px-4 py-2 text-xs font-medium uppercase text-amber-800">PO #</th>
                    <th className="text-left px-4 py-2 text-xs font-medium uppercase text-amber-800">Status</th>
                    <th className="w-32 px-4 py-2 text-xs font-medium uppercase text-amber-800">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-200">
                  {unscheduledDispatches.map(d => {
                    const statusInfo = getStatusInfo(d.status);
                    return (
                      <tr key={d.id} className="hover:bg-amber-100/50" data-testid={`unscheduled-row-${d.id}`}>
                        <td className="px-4 py-2 font-mono text-sm">{d.dispatch_number}</td>
                        <td className="px-4 py-2 text-sm font-medium">{d.customer_name}</td>
                        <td className="px-4 py-2 text-sm">{d.po_number || '-'}</td>
                        <td className="px-4 py-2">
                          <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                        </td>
                        <td className="px-4 py-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/sales-order/${d.id}`)}
                            className="text-amber-700 border-amber-400 hover:bg-amber-100"
                            data-testid={`btn-schedule-${d.id}`}
                          >
                            <CalendarDays className="w-3 h-3 mr-1" />
                            Schedule
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Month View - Calendar Grid */}
      {viewMode === 'month' && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">{monthName}</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="grid grid-cols-7 gap-1">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
              
              {Array.from({ length: (dateRange.start.getDay() + 6) % 7 }).map((_, i) => (
                <div key={`empty-${i}`} className="min-h-[80px] bg-muted/20 rounded" />
              ))}
              
              {daysInRange.map(day => {
                const dayStr = format(day, 'dd/MM/yyyy');
                const dayDispatches = dispatchesByDay[dayStr] || [];
                const isToday = isSameDay(day, new Date());
                
                return (
                  <div 
                    key={dayStr}
                    className={`min-h-[80px] border rounded p-1 ${isToday ? 'border-primary bg-primary/5' : 'border-muted'}`}
                  >
                    <div className={`text-xs font-medium mb-1 flex items-center justify-between gap-1 ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                      <span>{format(day, 'd')}</span>
                      {dayDispatches.length > 0 && (
                        <span className="font-mono font-semibold text-[#005F73]" data-testid={`month-units-${format(day, 'yyyy-MM-dd')}`}>
                          {fmtUnits(dayDispatches.reduce((s, d) => s + dispUnits(d), 0))} u
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {dayDispatches.slice(0, 3).map(d => {
                        const statusInfo = getStatusInfo(d.status);
                        return (
                          <div 
                            key={d.id}
                            className={`text-xs px-1 py-0.5 rounded truncate ${statusInfo.color}`}
                            title={`${d.customer_name} - ${d.dispatch_number}`}
                          >
                            {d.customer_name?.substring(0, 12)}
                          </div>
                        );
                      })}
                      {dayDispatches.length > 3 && (
                        <div className="text-xs text-muted-foreground px-1">
                          +{dayDispatches.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Day/Week View - Table */}
      {(viewMode === 'day' || viewMode === 'week') && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">
              {viewMode === 'day' ? `Orders for ${format(currentDate, 'EEEE, dd MMM')}` : `Orders for Week ${weekNumber}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredDispatches.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      {[
                        { key: 'po_number', label: 'PO #' },
                        { key: 'customer', label: 'Customer' },
                        { key: 'units', label: 'Units' },
                        { key: 'dispatch_date', label: 'Dispatch Date' },
                        { key: 'transport', label: 'Transport' },
                        { key: 'status', label: 'Status' },
                      ].map(col => {
                        const active = sortConfig.key === col.key;
                        const Icon = !active ? ArrowUpDown : (sortConfig.dir === 'asc' ? ChevronUp : ChevronDown);
                        return (
                          <th key={col.key} className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                            <button
                              type="button"
                              onClick={() => setSortConfig(prev => ({
                                key: col.key,
                                dir: prev.key === col.key && prev.dir === 'asc' ? 'desc' : 'asc',
                              }))}
                              className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${active ? 'text-foreground' : ''}`}
                              data-testid={`sort-${col.key}`}
                              title={`Sort by ${col.label}`}
                            >
                              <span>{col.label}</span>
                              <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
                            </button>
                          </th>
                        );
                      })}
                      <th className="w-48 px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredDispatches.map((dispatch) => {
                      const statusInfo = getStatusInfo(dispatch.status);
                      const StatusIcon = statusInfo.icon;
                      const nextStatus = getNextStatus(dispatch.status);
                      const prevStatus = getPrevStatus(dispatch.status);
                      const hasBatches = dispatch.lines?.some(l => l.batch_id);
                      const hasBatchDraft = !!(dispatch.batch_draft && Object.keys(dispatch.batch_draft.productBatches || {}).length > 0);
                      const isLocked = dispatch.status === 'locked';
                      const isDispatched = dispatch.status === 'dispatched';
                      
                      return (
                        <React.Fragment key={dispatch.id}>
                        <tr className="hover:bg-muted/30 transition-shadow" data-testid={`planner-row-${dispatch.id}`} data-dispatch-row={dispatch.id}>
                          {/* PO Number - Clickable */}
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openViewOrderDialog(dispatch)}
                              className="font-mono text-sm text-primary hover:underline cursor-pointer"
                              title="Click to view/edit order"
                              data-testid={`btn-po-${dispatch.id}`}
                            >
                              {dispatch.po_number || dispatch.dispatch_number}
                            </button>
                            {dispatch.po_group_id && (
                              <div>
                                <PoPartChip dispatch={dispatch} expanded={expandedPo.has(dispatch.id)} onToggle={() => toggleExpandedPo(dispatch.id)} />
                              </div>
                            )}
                            {hasBatches && (
                              <div className="flex items-center gap-1 text-xs text-blue-600 mt-0.5">
                                <Boxes className="w-3 h-3" />
                                <span>Batches</span>
                              </div>
                            )}
                            {hasBatchDraft && !isLocked && !isDispatched && (
                              <div
                                className="flex items-center gap-1 text-[10px] mt-0.5 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 w-fit"
                                title={`Partial batch entry saved${dispatch.batch_draft?.saved_at ? ' on ' + dispatch.batch_draft.saved_at.slice(0, 16).replace('T', ' ') : ''}. Click "Ready ›" to resume.`}
                                data-testid={`batch-draft-badge-${dispatch.id}`}
                              >
                                <FileText className="w-2.5 h-2.5" />
                                <span className="font-medium">Draft saved</span>
                              </div>
                            )}
                            {dispatch.has_returns && (
                              <div
                                className="flex items-center gap-1 text-[10px] mt-0.5 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 w-fit"
                                title="Stock was returned against this order — see Stock Returns register"
                                data-testid={`rtn-badge-${dispatch.id}`}
                              >
                                <Undo2 className="w-2.5 h-2.5" />
                                <span className="font-medium">RTN</span>
                              </div>
                            )}
                          </td>
                          {/* Customer */}
                          <td className="px-4 py-3">
                            <div className="font-medium text-sm">{dispatch.customer_name}</div>
                            <div className="text-xs text-muted-foreground">#{dispatch.dispatch_number}</div>
                          </td>
                          {/* Units */}
                          <td className="px-4 py-3 text-sm font-mono font-semibold" data-testid={`planner-units-${dispatch.id}`}>{fmtUnits(dispUnits(dispatch))}</td>
                          {/* Dispatch Date */}
                          <td className="px-4 py-3 text-sm font-mono">{showDate(dispatch.dispatch_date) || '-'}</td>
                          {/* Transport */}
                          <td className="px-4 py-3 text-sm">{dispatch.vehicle_info || '-'}</td>
                          {/* Status */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusInfo.label}
                              </span>
                              {isDispatched && (
                                <Lock className="w-3 h-3 text-muted-foreground" title="Dispatched - Status locked" />
                              )}
                              {/* AccountsIQ status pill — inline so operators don't have to open the 3-dots menu */}
                              {dispatch.accountsiq_last_error ? (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200"
                                  title={`AccountsIQ push failed: ${dispatch.accountsiq_last_error}`}
                                  data-testid={`aiq-pill-error-${dispatch.id}`}
                                >
                                  <Plug className="w-2.5 h-2.5" />
                                  AIQ
                                </span>
                              ) : dispatch.accountsiq_transaction_id ? (
                                <span
                                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                                    dispatch.accountsiq_status === 'duplicate'
                                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                                      : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  }`}
                                  title={`AccountsIQ tx: ${dispatch.accountsiq_transaction_id} (${dispatch.accountsiq_status || 'created'})${dispatch.accountsiq_pushed_at ? ' • ' + new Date(dispatch.accountsiq_pushed_at).toLocaleString('en-IE') : ''}`}
                                  data-testid={`aiq-pill-ok-${dispatch.id}`}
                                >
                                  <Plug className="w-2.5 h-2.5" />
                                  AIQ
                                </span>
                              ) : null}
                              {/* Invoice status pill — clickable to toggle */}
                              {['ready', 'locked', 'dispatched'].includes(dispatch.status) && canEdit(user?.role) && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleToggleInvoiced(dispatch); }}
                                  disabled={togglingInvoice === dispatch.id}
                                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border transition-colors ${
                                    dispatch.invoiced
                                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200'
                                      : 'bg-zinc-50 text-zinc-600 border-zinc-300 hover:bg-zinc-100'
                                  } ${togglingInvoice === dispatch.id ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                                  title={
                                    dispatch.invoiced
                                      ? `Invoiced (${dispatch.invoice_method === 'auto-aiq' ? 'auto-AIQ' : 'manual'})${dispatch.invoiced_by ? ' by ' + dispatch.invoiced_by : ''}${dispatch.invoiced_at ? ' • ' + new Date(dispatch.invoiced_at).toLocaleString('en-IE') : ''} — click to un-mark`
                                      : 'Not Invoiced — click to mark as invoiced'
                                  }
                                  data-testid={`invoice-pill-${dispatch.id}`}
                                >
                                  {togglingInvoice === dispatch.id
                                    ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                    : (dispatch.invoiced
                                      ? <Check className="w-2.5 h-2.5" />
                                      : <Circle className="w-2.5 h-2.5" />)}
                                  {dispatch.invoiced ? 'Invoiced' : 'Not Inv.'}
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="flex gap-1 items-center">
                              {/* View/Edit Button */}
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openViewOrderDialog(dispatch)}
                                className="h-7 px-2 text-xs"
                                title="View/Edit Order"
                                data-testid={`btn-view-order-${dispatch.id}`}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              
                              {/* Convert to Dispatch Note */}
                              {dispatch.status === 'locked' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => navigate(`/dispatch/${dispatch.id}`)}
                                  className="h-7 px-2 text-xs"
                                  title="Open Dispatch Note"
                                  data-testid={`btn-dispatch-${dispatch.id}`}
                                >
                                  <Truck className="w-3 h-3" />
                                </Button>
                              )}
                              
                              {/* Print PDF */}
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openDispatchPdf(dispatch.id)}
                                className="h-7 px-2 text-xs"
                                title="Print PDF"
                                data-testid={`btn-print-${dispatch.id}`}
                              >
                                <Printer className="w-3 h-3" />
                              </Button>
                              
                              {canEdit(user?.role) && (
                                <>
                                  {/* Back Button - with warning indicators */}
                                  {prevStatus && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      onClick={() => handleStatusChange(dispatch, prevStatus)}
                                      className={`h-7 px-2 text-xs ${isDispatched ? 'text-amber-600' : ''}`}
                                      title={isDispatched 
                                        ? `Go back (requires override permission)` 
                                        : hasBatches 
                                          ? `Back to ${prevStatus.replace('_', ' ')} (will affect batches)` 
                                          : `Back to ${prevStatus.replace('_', ' ')}`
                                      }
                                      data-testid={`btn-back-${dispatch.id}`}
                                    >
                                      <ChevronLeft className="w-3 h-3" />
                                      {hasBatches && !isDispatched && (
                                        <AlertTriangle className="w-2.5 h-2.5 text-amber-500" />
                                      )}
                                    </Button>
                                  )}
                                  
                                  {/* Forward Button */}
                                  {nextStatus && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleStatusChange(dispatch, nextStatus)}
                                      className="h-7 text-xs"
                                      data-testid={`btn-forward-${dispatch.id}`}
                                    >
                                      {nextStatus === 'in_progress' && 'Start'}
                                      {nextStatus === 'locked' && 'Ready'}
                                      {nextStatus === 'dispatched' && 'Dispatch'}
                                      <ChevronRight className="w-3 h-3 ml-1" />
                                    </Button>
                                  )}

                                  {/* Split Fulfilment — seasonal orders before Ready */}
                                  {dispatch.can_split_fulfilment && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setSplitTarget({ dispatch, prefill: null, fromReady: false })}
                                      className="h-7 px-2 text-xs text-primary"
                                      title="Split Fulfilment — send this order in more than one dispatch"
                                      data-testid={`btn-split-${dispatch.id}`}
                                    >
                                      <Split className="w-3 h-3" />
                                    </Button>
                                  )}
                                </>
                              )}
                              
                              {/* Row Menu */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-7 w-7 p-0"
                                    data-testid={`btn-menu-${dispatch.id}`}
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuItem onClick={() => openViewOrderDialog(dispatch)}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Order Details
                                  </DropdownMenuItem>
                                  {['draft', 'in_progress'].includes(dispatch.status) && (
                                    <DropdownMenuItem onClick={() => navigate(`/sales-order/${dispatch.id}`)}>
                                      <Edit className="w-4 h-4 mr-2" />
                                      Edit Order (Full Page)
                                    </DropdownMenuItem>
                                  )}
                                  {SPLITTABLE_STATUSES.includes(dispatch.status) && dispatch.destination_type !== 'event' && canEdit(user?.role) && (
                                    <DropdownMenuItem
                                      disabled={!dispatch.can_split_fulfilment}
                                      onClick={() => setSplitTarget({ dispatch, prefill: null, fromReady: false })}
                                      data-testid={`menu-split-fulfilment-${dispatch.id}`}
                                    >
                                      <Split className="w-4 h-4 mr-2 text-primary" />
                                      <span>Split Fulfilment</span>
                                      {!dispatch.can_split_fulfilment && (
                                        <span className="ml-auto pl-2 text-[10px] text-muted-foreground">no seasonal codes</span>
                                      )}
                                    </DropdownMenuItem>
                                  )}
                                  {dispatch.po_group_id && (
                                    <DropdownMenuItem
                                      onClick={() => setPoSummaryGroupId(dispatch.po_group_id)}
                                      data-testid={`menu-po-summary-${dispatch.id}`}
                                    >
                                      <GitBranch className="w-4 h-4 mr-2 text-primary" />
                                      PO Summary
                                    </DropdownMenuItem>
                                  )}
                                  {['locked', 'dispatched', 'ready'].includes(dispatch.status) && (
                                    <DropdownMenuItem
                                      onClick={() => navigate(`/dispatch/${dispatch.id}`)}
                                      data-testid={`btn-dispatch-details-${dispatch.id}`}
                                    >
                                      <FileText className="w-4 h-4 mr-2" />
                                      Dispatch Details
                                    </DropdownMenuItem>
                                  )}
                                  {['locked', 'ready', 'dispatched'].includes(dispatch.status) && canEdit(user?.role) && (
                                    <DropdownMenuItem
                                      onClick={() => openEditBatchData(dispatch)}
                                      data-testid={`btn-edit-batch-data-${dispatch.id}`}
                                    >
                                      <Edit className="w-4 h-4 mr-2" />
                                      Edit Batch Data
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      // For orders before batch creation, show "Order Not Processed" notice
                                      if (['draft', 'in_progress'].includes(dispatch.status)) {
                                        toast.info('Order preview not available - order not yet processed. Mark as Ready to generate dispatch note.');
                                      } else {
                                        openDispatchPdf(dispatch.id);
                                      }
                                    }}
                                  >
                                    <Printer className="w-4 h-4 mr-2" />
                                    {['draft', 'in_progress'].includes(dispatch.status) ? 'Preview (Not Ready)' : 'Print Dispatch Note'}
                                  </DropdownMenuItem>
                                  {!['draft', 'in_progress'].includes(dispatch.status) && (
                                    <DropdownMenuItem
                                      onClick={() => { setAccountantDispatch(dispatch); setAccountantOpen(true); }}
                                      data-testid={`btn-accountant-view-${dispatch.id}`}
                                    >
                                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                                      AccountsIQ View
                                    </DropdownMenuItem>
                                  )}
                                  {!['draft', 'in_progress'].includes(dispatch.status) && (
                                    <DropdownMenuItem
                                      onClick={() => openCoaPdf(dispatch.id)}
                                      data-testid={`coa-pdf-${dispatch.dispatch_number}`}
                                    >
                                      <ScrollText className="w-4 h-4 mr-2" />
                                      Certificate of Analysis
                                    </DropdownMenuItem>
                                  )}
                                  {['ready', 'locked', 'dispatched', 'completed'].includes(dispatch.status) && canEdit(user?.role) && (
                                    <DropdownMenuItem
                                      onClick={() => { setReturnDispatch(dispatch); setReturnDialogOpen(true); }}
                                      data-testid={`btn-record-return-${dispatch.id}`}
                                    >
                                      <Undo2 className="w-4 h-4 mr-2 text-amber-600" />
                                      Record Return
                                    </DropdownMenuItem>
                                  )}
                                  {dispatch.has_returns && (
                                    <DropdownMenuItem
                                      onClick={() => navigate(`/returns?search=${encodeURIComponent(dispatch.dispatch_number || '')}`)}
                                      data-testid={`btn-view-returns-${dispatch.id}`}
                                    >
                                      <FileText className="w-4 h-4 mr-2 text-amber-600" />
                                      View Return Notes
                                    </DropdownMenuItem>
                                  )}
                                  {!['draft', 'in_progress'].includes(dispatch.status) && (
                                    <DropdownMenuSub>
                                      <DropdownMenuSubTrigger>
                                        <Package className="w-4 h-4 mr-2" />
                                        Print Pallet Labels
                                      </DropdownMenuSubTrigger>
                                      <DropdownMenuSubContent>
                                        <DropdownMenuItem onClick={() => navigate(`/pallet-labels/${dispatch.id}`)} data-testid={`label-standard-${dispatch.dispatch_number}`}>
                                          <FileText className="w-4 h-4 mr-2" />
                                          Standard (Designer)
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => openPalletLabels(dispatch.id, 2, { template: 'agropur' })}
                                          data-testid={`label-agropur-${dispatch.dispatch_number}`}
                                        >
                                          <Package className="w-4 h-4 mr-2" />
                                          Agropur
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => {
                                            setAldiBranchDispatchId(dispatch.id);
                                            setAldiBranchOpen(true);
                                          }}
                                          data-testid={`label-aldi-${dispatch.dispatch_number}`}
                                        >
                                          <Package className="w-4 h-4 mr-2" />
                                          Aldi
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => openPalletLabels(dispatch.id, 1, { template: 'lidl' })}
                                          data-testid={`label-lidl-${dispatch.dispatch_number}`}
                                        >
                                          <Package className="w-4 h-4 mr-2" />
                                          Lidl (Shipment Note)
                                        </DropdownMenuItem>
                                      </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                  )}
                                  {!['draft', 'in_progress'].includes(dispatch.status) && (
                                    <DropdownMenuItem
                                      onClick={async () => {
                                        await openDispatchPdf(dispatch.id);
                                        setTimeout(() => {
                                          navigate(`/pallet-labels/${dispatch.id}`);
                                        }, 500);
                                      }}
                                    >
                                      <Printer className="w-4 h-4 mr-2" />
                                      Print All (Note + Labels)
                                    </DropdownMenuItem>
                                  )}
                                  {!['draft', 'in_progress'].includes(dispatch.status) && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuSub>
                                        <DropdownMenuSubTrigger data-testid={`menu-email-${dispatch.id}`}>
                                          <Mail className="w-4 h-4 mr-2 text-blue-600" />
                                          Email
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent>
                                          <DropdownMenuItem
                                            onClick={() => openEmailCompose(dispatch, 'dispatch_note')}
                                            data-testid={`email-dispatch-note-${dispatch.id}`}
                                          >
                                            <FileText className="w-4 h-4 mr-2" />
                                            Email Dispatch Note
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => openEmailCompose(dispatch, 'pallet_label')}
                                            data-testid={`email-pallet-label-${dispatch.id}`}
                                          >
                                            <Package className="w-4 h-4 mr-2" />
                                            Email Pallet Label
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => openEmailCompose(dispatch, 'coa')}
                                            data-testid={`email-coa-${dispatch.id}`}
                                          >
                                            <ScrollText className="w-4 h-4 mr-2" />
                                            Email Certificate of Analysis
                                          </DropdownMenuItem>
                                        </DropdownMenuSubContent>
                                      </DropdownMenuSub>
                                      <DropdownMenuItem
                                        onClick={() => setAiqPreviewDispatch(dispatch)}
                                        data-testid={`btn-push-accountsiq-${dispatch.id}`}
                                      >
                                        <Plug className="w-4 h-4 mr-2 text-indigo-600" />
                                        {dispatch.accountsiq_transaction_id
                                          ? `AccountsIQ draft #${(dispatch.accountsiq_transaction_id || '').slice(0, 10)}`
                                          : 'Preview & send to AccountsIQ'}
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                        {dispatch.po_group_id && expandedPo.has(dispatch.id) && (
                          <tr className="bg-muted/10" data-testid={`po-tree-row-${dispatch.id}`}>
                            <td colSpan={7} className="px-4 pb-3 pt-0">
                              <PoPartsTree
                                order={dispatch}
                                onOpenPart={openPartById}
                                onOpenSummary={(o) => setPoSummaryGroupId(o.po_group_id)}
                              />
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-2">No orders for this {viewMode}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first sales order to get started
                </p>
                {canEdit(user?.role) && (
                  <Button onClick={() => navigate('/sales-order/new')}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Sales Order
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      </>
      )}

      {/* Record Return dialog (shared core component) */}
      <RecordReturnDialog
        dispatch={returnDispatch}
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        onSuccess={() => loadData()}
      />

      {/* Create Order Dialog - Using Extracted Component */}
      <CreateOrderDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        formData={formData}
        setFormData={setFormData}
        selectedDate={selectedDate}
        handleDateSelect={handleDateSelect}
        customers={customers}
        products={products}
        cobrandings={cobrandings}
        showCreateCustomer={showCreateCustomer}
        setShowCreateCustomer={setShowCreateCustomer}
        showCreateProduct={showCreateProduct}
        setShowCreateProduct={setShowCreateProduct}
        showCreateCoBranding={showCreateCoBranding}
        setShowCreateCoBranding={setShowCreateCoBranding}
        newCustomerName={newCustomerName}
        setNewCustomerName={setNewCustomerName}
        newProductName={newProductName}
        setNewProductName={setNewProductName}
        newProductWeight={newProductWeight}
        setNewProductWeight={setNewProductWeight}
        newProductUnits={newProductUnits}
        setNewProductUnits={setNewProductUnits}
        newCoBrandingName={newCoBrandingName}
        setNewCoBrandingName={setNewCoBrandingName}
        creatingCustomer={creatingCustomer}
        creatingProduct={creatingProduct}
        creatingCoBranding={creatingCoBranding}
        handleCreateCustomer={handleCreateCustomer}
        handleCreateProduct={handleCreateProduct}
        handleCreateCoBranding={handleCreateCoBranding}
        handleCreate={handleCreate}
        saving={saving}
      />

      {/* Ready Status Dialog - Using Extracted Component */}
      <BatchCreationDialog
        open={readyDialogOpen}
        onOpenChange={(open) => {
          setReadyDialogOpen(open);
          if (!open) {
            setBatchStep(1);
            setProductBatches({});
            setProductUseDates({});
            setOutOfStockProducts({});
            setEditingBatchData(false);
            setPalletCountReady('');
          }
        }}
        pendingReadyDispatch={pendingReadyDispatch}
        products={products}
        batches={batches}
        cobrandings={cobrandings}
        batchStep={batchStep}
        setBatchStep={setBatchStep}
        productBatches={productBatches}
        setProductBatches={setProductBatches}
        productUseDates={productUseDates}
        setProductUseDates={setProductUseDates}
        productNotes={productNotes}
        setProductNotes={setProductNotes}
        outOfStockProducts={outOfStockProducts}
        setOutOfStockProducts={setOutOfStockProducts}
        batchLines={batchLines}
        setBatchLines={setBatchLines}
        getTotalBatchQty={getTotalBatchQty}
        validateBatchCodes={validateBatchCodes}
        validateBatchValues={validateBatchValues}
        validateUseDates={validateUseDates}
        generateFinalBatchLines={generateFinalBatchLines}
        handleReadyWithBatch={handleReadyWithBatch}
        creatingBatch={creatingBatch}
        handleSaveDraft={handleSaveBatchDraft}
        savingDraft={savingDraft}
        showDom={!!customers.find(c => c.id === pendingReadyDispatch?.customer_id)?.print_dom_on_dispatch}
        customerAutoCalcWeight={!!customers.find(c => c.id === pendingReadyDispatch?.customer_id)?.auto_calc_weight}
        editingBatchData={editingBatchData}
        palletCount={palletCountReady}
        setPalletCount={setPalletCountReady}
        onSplitFulfilment={pendingReadyDispatch?.can_split_fulfilment && !editingBatchData ? openSplitFromReady : undefined}
      />

      {/* Quick Dispatch Dialog - Using Extracted Component */}
      <QuickDispatchDialog
        open={quickDispatchOpen}
        onOpenChange={setQuickDispatchOpen}
        dispatchLines={dispatchLines}
        products={products}
        batches={batches}
        cobrandings={cobrandings}
        getBatchesForProduct={getBatchesForProduct}
        addDispatchLine={addDispatchLine}
        updateDispatchLine={updateDispatchLine}
        removeDispatchLine={removeDispatchLine}
        handleQuickDispatch={handleQuickDispatch}
        saving={saving}
      />


      {/* View/Edit Order Dialog - Using Extracted Component */}
      <ViewOrderDialog
        open={viewOrderDialogOpen}
        onOpenChange={setViewOrderDialogOpen}
        selectedOrder={selectedOrder}
        editingOrder={editingOrder}
        setEditingOrder={setEditingOrder}
        orderFormData={orderFormData}
        setOrderFormData={setOrderFormData}
        customers={customers}
        products={products}
        cobrandings={cobrandings}
        canEdit={canEdit}
        canEditOrderFully={canEditOrderFully}
        canEditOrderHeader={canEditOrderHeader}
        handleSaveOrder={handleSaveOrder}
        savingOrder={savingOrder}
        userRole={user?.role}
        onSplitFulfilment={(order) => { setViewOrderDialogOpen(false); setSplitTarget({ dispatch: order, prefill: null, fromReady: false }); }}
        onOpenPart={openPartById}
        onOpenPoSummary={(order) => { setViewOrderDialogOpen(false); setPoSummaryGroupId(order.po_group_id); }}
      />
      <SplitFulfilmentDialog
        open={!!splitTarget}
        onOpenChange={(open) => { if (!open) closeSplit(); }}
        dispatch={splitTarget?.dispatch}
        prefill={splitTarget?.prefill}
        onDone={handleSplitDone}
      />
      <PoSummaryDialog
        groupId={poSummaryGroupId}
        onOpenChange={(open) => { if (!open) setPoSummaryGroupId(null); }}
        canMerge={['super_admin', 'admin'].includes(user?.role)}
        onChanged={() => loadData()}
      />
      {/* Status Revert Dialog - Using Extracted Component */}
      <RevertStatusDialog
        open={revertDialogOpen}
        onOpenChange={setRevertDialogOpen}
        pendingRevertDispatch={pendingRevertDispatch}
        pendingRevertStatus={pendingRevertStatus}
        revertCheckResult={revertCheckResult}
        batchAction={batchAction}
        setBatchAction={setBatchAction}
        batchDeleteReason={batchDeleteReason}
        setBatchDeleteReason={setBatchDeleteReason}
        overrideReason={overrideReason}
        setOverrideReason={setOverrideReason}
        handleRevertConfirm={handleRevertConfirm}
        reverting={reverting}
      />

      {/* Uplift Date Dialog */}
      <Dialog open={upliftDialogOpen} onOpenChange={(open) => { if (!open) { setUpliftDialogOpen(false); setUpliftDispatchRef(null); } }}>
        <DialogContent className="sm:max-w-[400px]" data-testid="uplift-date-dialog">
          <DialogHeader>
            <DialogTitle>Set Uplift Date</DialogTitle>
            <DialogDescription>
              When will this order be collected/uplifted?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <DatePicker
              value={upliftDate}
              onChange={setUpliftDate}
              placeholder="Select uplift date"
              data-testid="input-uplift-date"
            />
            {upliftDispatchRef?.dispatch_date && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setUpliftDate(upliftDispatchRef.dispatch_date)}
                data-testid="btn-same-as-dispatch"
              >
                Same as Dispatch Date ({upliftDispatchRef.dispatch_date})
              </Button>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setUpliftDialogOpen(false); setUpliftDispatchRef(null); }}>
              Cancel
            </Button>
            <Button
              data-testid="btn-confirm-dispatch"
              onClick={async () => {
                if (!upliftDispatchRef) return;
                try {
                  await updateDispatchStatus(upliftDispatchRef.id, 'dispatched', null, null, upliftDate || undefined);
                  toast.success('Dispatch completed');
                  setUpliftDialogOpen(false);
                  setEmailPromptDispatch(upliftDispatchRef);
                  setEmailPromptOpen(true);
                  setUpliftDispatchRef(null);
                  loadData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to dispatch');
                }
              }}
            >
              Confirm Dispatch
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Prompt Dialog */}
      <EmailPromptDialog
        open={emailPromptOpen}
        onOpenChange={setEmailPromptOpen}
        dispatch={emailPromptDispatch}
        onYes={handleEmailPromptYes}
        onNo={handleEmailPromptNo}
      />

      {/* Email Compose Dialog */}
      <EmailComposeDialog
        open={emailComposeOpen}
        onOpenChange={setEmailComposeOpen}
        dispatch={emailPromptDispatch}
        emailData={emailData}
        setEmailData={setEmailData}
        ccInput={emailCcInput}
        setCcInput={setEmailCcInput}
        onAddCc={handleAddEmailCc}
        onRemoveCc={handleRemoveEmailCc}
        onSend={handleSendDispatchEmail}
        sending={sendingEmail}
        onCancel={() => { setEmailComposeOpen(false); setEmailPromptDispatch(null); setEmailDocType('dispatch_note'); setEmailRecipientSuggestions([]); }}
        docType={emailDocType}
        suggestedRecipients={emailRecipientSuggestions}
      />

      <AldiBranchDialog
        open={aldiBranchOpen}
        onClose={() => setAldiBranchOpen(false)}
        dispatchId={aldiBranchDispatchId}
      />

      <AccountantViewDialog
        open={accountantOpen}
        onOpenChange={setAccountantOpen}
        dispatch={accountantDispatch}
        onInvoiceMarked={(dispatchId, data) => {
          // Optimistically update so the row chip in any list view
          // reflects the new invoice number without a full reload.
          setDispatches((prev) => Array.isArray(prev)
            ? prev.map((d) => d.id === dispatchId ? { ...d, ...data, dispatch_number: d.dispatch_number } : d)
            : prev);
          setAccountantDispatch((d) => d && d.id === dispatchId ? { ...d, ...data } : d);
        }}
      />
      <InvoicePreviewDialog
        open={!!aiqPreviewDispatch}
        onOpenChange={(v) => { if (!v) setAiqPreviewDispatch(null); }}
        dispatch={aiqPreviewDispatch}
        onSent={() => loadData()}
      />
    </div>
  );
}
